// Modals.tsx — All modal components for SK_TICKETS
// @ts-nocheck
import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  View, Text, TextInput, TouchableOpacity, ScrollView,
  StyleSheet, Modal as RNModal, Platform, Pressable, Animated, Easing,
} from 'react-native';
import { Colors, FontSize, FontWeight, Radii, Shadow } from '../theme/tokens';
import { Icon } from './Icon';
import { Avatar } from './Avatar';
import PrimaryButton from './Button_Component/Primary_Button';
import Email_Input from './Email_Input/Email_Input';
import MobileNumberInput, { COUNTRIES, CountryEntry } from './Mobile_Number/Mobile_Number';
import CommonInput from './Common_Input/Common_Input';
import { useApp } from '../store/AppStore';
import { useAuth } from '../store/AuthContext';
import { contactsService } from '../services/contacts/contacts.service';

// Default dial country for the Add-customer card (India), with a safe fallback.
const DEFAULT_COUNTRY: CountryEntry = COUNTRIES.find(c => c.code === 'IN') || COUNTRIES[0];

// ─────────── RequiredStar ───────────
function RequiredStar() {
  return <Text style={{ color: Colors.danger, marginLeft: 3, fontSize: 17, fontWeight: '800', lineHeight: 17 }}>*</Text>;
}

// ─────────── FieldLabel — icon-led label for premium forms ───────────
function FieldLabel({ icon, children, required, hint }: { icon: string; children: React.ReactNode; required?: boolean; hint?: string }) {
  return (
    <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6, marginBottom: 7 }}>
      <Icon name={icon as any} size={13} color={Colors.ink3} stroke={1.9} />
      <Text style={S.fieldLabel}>{children}</Text>
      {required && <RequiredStar />}
      {hint ? <Text style={{ color: Colors.ink4, fontSize: 12, fontWeight: FontWeight.regular }}> {hint}</Text> : null}
    </View>
  );
}

// ─────────── Base Modal Wrapper ───────────
export function Modal({ visible, onClose, width = 560, children, maxHeight = '90%' }) {
  if (!visible) return null;
  return (
    <View style={S.backdrop}>
      <Pressable style={StyleSheet.absoluteFill} onPress={onClose} />
      <View style={[S.modalBox, { width, maxHeight: typeof maxHeight === 'string' ? undefined : maxHeight },
        Platform.OS === 'web' ? { maxHeight: maxHeight } as any : {}]}>
        {children}
      </View>
    </View>
  );
}

// ─────────── TagInput ───────────
export function TagInput({ tags, onChange, placeholder = 'Add tag…' }) {
  const [draft, setDraft] = useState('');
  const commit = (raw) => {
    const v = (raw || '').trim().replace(/,$/, '');
    if (!v || tags.includes(v)) { setDraft(''); return; }
    onChange([...tags, v]);
    setDraft('');
  };
  return (
    <View>
      <View style={S.tagInputBox}>
        {tags.map((t, i) => (
          <TouchableOpacity key={t + i} onPress={() => onChange(tags.filter((_, j) => j !== i))} style={S.tagChip}>
            <Text style={S.tagChipText}>{t}</Text>
            <Icon name="X" size={10} color={Colors.ink3} />
          </TouchableOpacity>
        ))}
        <TextInput
          value={draft}
          placeholder={tags.length === 0 ? placeholder : ''}
          placeholderTextColor={Colors.ink4}
          onChangeText={(v) => { if (v.endsWith(',')) commit(v); else setDraft(v); }}
          onSubmitEditing={() => commit(draft)}
          onBlur={() => commit(draft)}
          onKeyPress={Platform.OS === 'web' ? ({ nativeEvent }) => {
            if (nativeEvent.key === 'Enter') commit(draft);
            if (nativeEvent.key === 'Backspace' && draft === '' && tags.length > 0)
              onChange(tags.slice(0, -1));
          } : undefined}
          style={S.tagDraftInput}
        />
      </View>
      <Text style={S.tagHint}>Press Enter or comma to add. Tap a tag to remove.</Text>
    </View>
  );
}

// ─────────── MultiInput ───────────
export function MultiInput({ values, onChange, placeholder, type, addLabel }) {
  const set = (i, v) => { const next = [...values]; next[i] = v; onChange(next); };
  const remove = (i) => {
    if (values.length <= 1) { onChange(['']); return; }
    onChange(values.filter((_, j) => j !== i));
  };
  return (
    <View style={{ gap: 8 }}>
      {values.map((v, i) => (
        <View key={i} style={S.multiRow}>
          <TextInput
            value={v}
            placeholder={placeholder}
            placeholderTextColor={Colors.ink4}
            onChangeText={(val) => set(i, val)}
            keyboardType={type === 'tel' ? 'phone-pad' : type === 'email' ? 'email-address' : 'default'}
            style={[S.input, S.inputMono, { flex: 1 }]}
          />
          {values.length > 1 && (
            <TouchableOpacity onPress={() => remove(i)} style={S.iconBtn}>
              <Icon name="X" size={13} color={Colors.ink3} />
            </TouchableOpacity>
          )}
        </View>
      ))}
      <TouchableOpacity onPress={() => onChange([...values, ''])}>
        <Text style={{ color: Colors.info, fontSize: 12.5, fontWeight: FontWeight.medium }}>+ {addLabel}</Text>
      </TouchableOpacity>
    </View>
  );
}

// ─────────── SegmentedPicker ───────────
export function SegmentedPicker({ value, onChange, options }) {
  return (
    <View style={S.segmented}>
      {options.map(o => {
        const active = o.v === value;
        return (
          <TouchableOpacity key={o.v} onPress={() => onChange(o.v)}
            style={[S.segBtn, active && S.segBtnActive]}>
            {o.icon && <View style={{ marginRight: 3 }}>{o.icon}</View>}
            <Text style={[S.segBtnText, active && S.segBtnTextActive,
              active && o.color ? { color: o.color } : {}]}>
              {o.label}
            </Text>
          </TouchableOpacity>
        );
      })}
    </View>
  );
}

// ─────────── PickerCard ───────────
export function PickerCard({ active, title, sub, onClick, onPress, disabled, badge }) {
  const handler = onPress || onClick;
  return (
    <TouchableOpacity onPress={handler} disabled={disabled}
      style={[S.pickerCard, active && S.pickerCardActive, disabled && { opacity: 0.5 }]}>
      {badge && <View style={S.pickerBadge}><Text style={S.pickerBadgeText}>{badge}</Text></View>}
      <Text style={S.pickerCardTitle}>{title}</Text>
      {sub && <Text style={S.pickerCardSub}>{sub}</Text>}
    </TouchableOpacity>
  );
}

// ─────────── Toggle ───────────
function Toggle({ on, onPress }) {
  return (
    <TouchableOpacity onPress={onPress} style={[S.toggle, on && S.toggleOn]}>
      <View style={[S.toggleThumb, on && S.toggleThumbOn]} />
    </TouchableOpacity>
  );
}

// ─────────── SectionBlock (for RuleEditor) ───────────
function SectionBlock({ label, colour, children }) {
  return (
    <View style={[S.sectionBlock, { borderColor: Colors.line }]}>
      <View style={[S.sectionBlockHead, { backgroundColor: `${colour}14` }]}>
        <View style={[S.sectionBlockDot, { backgroundColor: colour }]} />
        <Text style={[S.sectionBlockLabel]}>{label}</Text>
      </View>
      <View style={{ padding: 14 }}>{children}</View>
    </View>
  );
}

// ─────────── CheckboxRow ───────────
function CheckboxRow({ value = [], onChange, options }) {
  const toggle = (k) => {
    const next = value.includes(k) ? value.filter(x => x !== k) : [...value, k];
    onChange(next);
  };
  return (
    <View style={{ gap: 4 }}>
      {options.map(([k, l]) => {
        const on = value.includes(k);
        return (
          <TouchableOpacity key={k} onPress={() => toggle(k)}
            style={[S.checkRow, on && { backgroundColor: Colors.accentSoft }]}>
            <View style={[S.checkbox, on && S.checkboxOn]}>
              {on && <Icon name="Check" size={10} color="#fff" />}
            </View>
            <Text style={[S.checkRowText, on && { color: Colors.ink, fontWeight: FontWeight.medium }]}>{l}</Text>
          </TouchableOpacity>
        );
      })}
    </View>
  );
}

// ─────────── FilterGroup ───────────
function FilterGroup({ label, children }) {
  return (
    <View>
      <Text style={S.filterGroupLabel}>{label}</Text>
      {children}
    </View>
  );
}

// ─────────── ColumnPicker ───────────
function ColumnPicker({ groups, selected, onChange }) {
  const allKeys = groups.flatMap(g => g.cols.map(c => c.key));
  const setAll = () => onChange(allKeys);
  const reset = () => onChange(groups.flatMap(g => g.cols.filter(c => c.default !== false).map(c => c.key)));
  const clear = () => onChange([]);
  const toggle = (k) => onChange(selected.includes(k) ? selected.filter(x => x !== k) : [...selected, k]);
  return (
    <View style={S.columnPicker}>
      <View style={S.columnPickerHead}>
        <Text style={S.columnPickerHeadText}>Columns · <Text style={{ color: Colors.ink3 }}>{selected.length} selected</Text></Text>
        <View style={{ flex: 1 }} />
        <TouchableOpacity onPress={setAll}><Text style={{ color: Colors.info, fontSize: 12.5, fontWeight: FontWeight.medium, paddingHorizontal: 6 }}>Select all</Text></TouchableOpacity>
        <TouchableOpacity onPress={reset}><Text style={{ color: Colors.ink2, fontSize: 12.5, fontWeight: FontWeight.medium, paddingHorizontal: 6 }}>Reset</Text></TouchableOpacity>
        <TouchableOpacity onPress={clear}><Text style={{ color: Colors.danger, fontSize: 12.5, fontWeight: FontWeight.medium, paddingHorizontal: 6 }}>Clear</Text></TouchableOpacity>
      </View>
      <View style={{ gap: 14 }}>
        {groups.map((g, gi) => (
          <View key={gi}>
            {g.label && <Text style={S.columnGroupLabel}>{g.label.toUpperCase()}</Text>}
            <View style={S.columnGrid}>
              {g.cols.map(c => {
                const on = selected.includes(c.key);
                return (
                  <TouchableOpacity key={c.key} onPress={() => toggle(c.key)} style={S.columnItem}>
                    <View style={[S.checkbox, on && S.checkboxInfo]}>
                      {on && <Icon name="Check" size={11} color="#fff" />}
                    </View>
                    <Text style={{ flex: 1, fontSize: 13.5, color: Colors.ink }}>{c.label}</Text>
                    <Text style={{ fontFamily: Platform.OS === 'web' ? 'monospace' : undefined, color: Colors.ink4, fontSize: 11.5 }}>{c.slug}</Text>
                  </TouchableOpacity>
                );
              })}
            </View>
          </View>
        ))}
      </View>
    </View>
  );
}

// ─────────── FieldPreview ───────────
function FieldPreview({ label, required, type, placeholder, options, defaultValue, description }) {
  return (
    <View style={{ gap: 4 }}>
      <View style={{ flexDirection: 'row' }}>
        <Text style={{ fontSize: 13, fontWeight: FontWeight.semibold, color: Colors.ink }}>{label}</Text>
        {required && <RequiredStar />}
      </View>
      {type === 'Boolean' ? (
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
          <Toggle on={defaultValue === 'true'} onPress={() => {}} />
          <Text style={{ fontSize: 13, color: Colors.ink2 }}>Yes / No</Text>
        </View>
      ) : type === 'Select' ? (
        <View style={[S.input, { justifyContent: 'center' }]}>
          <Text style={{ color: Colors.ink4, fontSize: 13 }}>{defaultValue || 'Select…'}</Text>
        </View>
      ) : (
        <View style={[S.input, { justifyContent: 'center' }]}>
          <Text style={{ color: Colors.ink4, fontSize: 13 }}>{placeholder || (type === 'Date' ? 'YYYY-MM-DD' : 'Example value')}</Text>
        </View>
      )}
      {description ? <Text style={{ fontSize: 12, color: Colors.ink3 }}>{description}</Text> : null}
    </View>
  );
}

// ─────────────────────────────────────────────────
//  MODALS
// ─────────────────────────────────────────────────

// ─────────── AddCustomerCard ───────────
// Inline card surfaced inside the New-ticket flow when a customer search finds
// no match. Collects Name + Email (required) + Mobile and creates the contact
// on SK-CRM through the Backbone gateway (POST /v1/contacts → Backbone → CRM),
// then hands the created record back so it can be attached to the ticket.
function AddCustomerCard({ initialName = '', initialEmail = '', onClose, onCreated }) {
  const [name, setName] = useState(initialName);
  const [email, setEmail] = useState(initialEmail);
  const [country, setCountry] = useState<CountryEntry>(DEFAULT_COUNTRY);
  const [mobile, setMobile] = useState('');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const [nameError, setNameError] = useState('');
  const [emailTouched, setEmailTouched] = useState(false);
  const [mobileTouched, setMobileTouched] = useState(false);

  // Full name — letters and spaces only. Any digit/symbol is rejected (and
  // stripped) with a message naming what was disallowed.
  const handleNameChange = (t: string) => {
    const bad = t.match(/[^A-Za-z ]/);
    if (bad) {
      setNameError(/[0-9]/.test(bad[0]) ? 'Numbers are not allowed.' : `Special character "${bad[0]}" is not allowed.`);
    } else {
      setNameError('');
    }
    setName(t.replace(/[^A-Za-z ]/g, ''));
  };

  // ── Entrance choreography ──────────────────────────────────────────
  // Card lifts + fades in; the icon springs in with a slight rotate; the three
  // fields cascade in with a staggered slide. Pure-JS driver on web.
  const native = Platform.OS !== 'web';
  const enter = useRef(new Animated.Value(0)).current;
  const iconPop = useRef(new Animated.Value(0)).current;
  const fields = useRef([0, 1, 2].map(() => new Animated.Value(0))).current;
  useEffect(() => {
    Animated.parallel([
      Animated.timing(enter, { toValue: 1, duration: 380, easing: Easing.out(Easing.cubic), useNativeDriver: native }),
      Animated.sequence([
        Animated.delay(110),
        Animated.spring(iconPop, { toValue: 1, friction: 5, tension: 150, useNativeDriver: native }),
      ]),
      Animated.stagger(85, fields.map(v =>
        Animated.timing(v, { toValue: 1, duration: 320, easing: Easing.out(Easing.cubic), useNativeDriver: native }),
      )),
    ]).start();
  }, []);

  const cardStyle = {
    opacity: enter,
    transform: [
      { translateY: enter.interpolate({ inputRange: [0, 1], outputRange: [20, 0] }) },
      { scale: enter.interpolate({ inputRange: [0, 1], outputRange: [0.96, 1] }) },
    ],
  };
  const iconStyle = {
    opacity: iconPop,
    transform: [
      { scale: iconPop.interpolate({ inputRange: [0, 1], outputRange: [0.3, 1] }) },
      { rotate: iconPop.interpolate({ inputRange: [0, 1], outputRange: ['-30deg', '0deg'] }) },
    ],
  };
  const fieldStyle = (v: Animated.Value) => ({
    opacity: v,
    transform: [{ translateY: v.interpolate({ inputRange: [0, 1], outputRange: [12, 0] }) }],
  });

  // Email — only letters, digits and . _ - + @ are permitted. Any other symbol
  // is flagged by name; otherwise standard-format rules apply.
  const emailTrim = email.trim();
  const emailBadChar = email.match(/[^A-Za-z0-9._+@-]/);
  const emailFormatOk = /^[A-Za-z0-9._+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/.test(emailTrim);
  const emailOk = !emailBadChar && emailFormatOk;
  const emailError = emailBadChar
    ? `Character "${emailBadChar[0]}" is not allowed.`
    : (emailTouched
        ? (!emailTrim ? 'Email is required.' : (!emailFormatOk ? 'Enter a valid email address.' : undefined))
        : undefined);

  const mobileDigits = mobile.replace(/\D/g, '');
  // Mobile is optional; if entered it must match the country's digit count.
  const mobileOk = mobileDigits.length === 0 || mobileDigits.length === country.digitCount;
  const canSave = emailOk && mobileOk && !saving;

  const submit = async () => {
    if (!canSave) { setEmailTouched(true); setMobileTouched(true); return; }
    setSaving(true);
    setErr('');
    const phone = mobileDigits ? `${country.dialCode}${mobileDigits}` : null;
    try {
      const created = await contactsService.create({
        displayName: name.trim() || email.trim(),
        email: email.trim().toLowerCase(),
        phone,
      });
      onCreated(created, { name: name.trim(), email: email.trim().toLowerCase(), phone });
    } catch (e: any) {
      setErr(e?.response?.data?.message || e?.message || 'Could not create the customer. Please try again.');
      setSaving(false);
    }
  };

  return (
    <Animated.View style={[S.addCard, cardStyle]}>
      {/* Elite accent strip */}
      <View style={S.addCardAccent} />

      {/* Header */}
      <View style={S.addCardHead}>
        <Animated.View style={[S.addCardIcon, iconStyle]}>
          <Icon name="User" size={17} color={Colors.accent} stroke={1.9} />
        </Animated.View>
        <View style={{ flex: 1, minWidth: 0 }}>
          <Text style={S.addCardTitle}>Add a new customer</Text>
        </View>
        <TouchableOpacity onPress={onClose} style={S.iconBtn}>
          <Icon name="X" size={14} color={Colors.ink2} />
        </TouchableOpacity>
      </View>

      {/* Fields */}
      <View style={S.addCardBody}>
        <Animated.View style={fieldStyle(fields[0])}>
          <CommonInput
            dense
            label="Full name"
            placeholder="Enter Customer name"
            value={name}
            onChangeText={handleNameChange}
            touched={!!nameError}
            error={nameError || undefined}
          />
        </Animated.View>
        <Animated.View style={fieldStyle(fields[1])}>
          <Email_Input
            dense
            required
            label="Email"
            value={email}
            onValueChange={(val) => { setEmail(val); if (/[^A-Za-z0-9._+@-]/.test(val)) setEmailTouched(true); }}
            onBlur={() => setEmailTouched(true)}
            touched={emailTouched || !!emailBadChar}
            error={emailError}
          />
        </Animated.View>
        <Animated.View style={fieldStyle(fields[2])}>
          <MobileNumberInput
            dense
            label="Mobile number"
            value={mobile}
            selectedCountry={country}
            onCountryChange={(c) => { setCountry(c); setMobile(''); }}
            onChangeText={setMobile}
            onBlur={() => setMobileTouched(true)}
            placeholder="Enter the mobile number"
            error={mobileTouched && !mobileOk ? `Enter ${country.digitCount} digits for ${country.name}.` : undefined}
          />
        </Animated.View>
        {err ? <Text style={S.addCardErr}>{err}</Text> : null}
      </View>

      {/* Footer */}
      <View style={S.addCardFoot}>
        <PrimaryButton size="sm" variant="default" onPress={onClose} disabled={saving}>Cancel</PrimaryButton>
        <PrimaryButton size="sm" variant="primary" loading={saving} disabled={!canSave}
          leftIcon={saving ? undefined : <Icon name="Check" size={13} color="#fff" />}
          onPress={submit}>Create customer</PrimaryButton>
      </View>
    </Animated.View>
  );
}

// ─────────── NewTicketModal ───────────
export function NewTicketModal({ open, onCancel, onCreate, initialCustomer = null }) {
  const { contacts: appContacts, loadContacts, toast } = useApp();
  const [subject, setSubject] = useState('');
  // The contact picked from the list (or just created) — its id and phone go
  // with the ticket so it belongs to that contact (lib/customerLink).
  const [selected, setSelected] = useState(null);
  const [customerEmail, setCustomerEmail] = useState('');
  const [customerName, setCustomerName] = useState('');
  const [customerOrg, setCustomerOrg] = useState('');
  const [channel, setChannel] = useState('email');
  const [priority, setPriority] = useState('medium');
  const [category, setCategory] = useState('');
  const [body, setBody] = useState('');
  const [contactPickerOpen, setContactPickerOpen] = useState(false);
  // Inline "Add customer" card — opens when search finds no match. Creates the
  // customer on SK-CRM (via Backbone) before it's attached to the ticket.
  const [addCustomerOpen, setAddCustomerOpen] = useState(false);

  // Use real contacts from AppStore (derived from real tickets)
  const knownContacts = (appContacts || []).map(c => ({
    _contactId: c._contactId || null,
    name: c.name,
    email: c.email || '',
    phone: c.phone || '',
    org: c.org || '—',
    initials: c.initials,
    color: c.color,
  }));

  useEffect(() => {
    if (!open) {
      setSubject(''); setCustomerEmail(''); setCustomerName(''); setCustomerOrg('');
      setChannel('email'); setPriority('medium'); setCategory(''); setBody('');
      setContactPickerOpen(false); setAddCustomerOpen(false); setSelected(null);
    } else if (initialCustomer) {
      pick(initialCustomer);
    }
  }, [open]);

  // Entrance: backdrop dims in, the card lifts + scales up (portrait-style focus).
  const enter = useRef(new Animated.Value(0)).current;
  useEffect(() => {
    if (open) {
      enter.setValue(0);
      Animated.timing(enter, { toValue: 1, duration: 300, easing: Easing.out(Easing.cubic), useNativeDriver: Platform.OS !== 'web' }).start();
    }
  }, [open]);

  if (!open) return null;

  const backdropAnim = { opacity: enter };
  const boxAnim = {
    opacity: enter,
    transform: [
      { translateY: enter.interpolate({ inputRange: [0, 1], outputRange: [18, 0] }) },
      { scale: enter.interpolate({ inputRange: [0, 1], outputRange: [0.97, 1] }) },
    ],
  };

  const query = customerEmail.toLowerCase();
  const filtered = customerEmail.length > 0
    ? knownContacts.filter(c =>
        c.email.toLowerCase().includes(query) ||
        c.name.toLowerCase().includes(query) ||
        (c.phone && c.phone.includes(customerEmail.trim())))
    : knownContacts;

  // The field shows the contact's email, or its phone when it has none.
  function pick(c) {
    setSelected({ _contactId: c._contactId || null, email: c.email || '', phone: c.phone || '', name: c.name });
    setCustomerEmail(c.email || c.phone || c.name);
    setCustomerName(c.name);
    setCustomerOrg(c.org && c.org !== '—' ? c.org : '');
    setContactPickerOpen(false);
  }

  const isKnown = selected || knownContacts.find(c => c.email && c.email === customerEmail);
  const emailLooksValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(customerEmail.trim());
  // A new customer needs a valid email; a picked existing contact is fine as-is.
  const canSubmit = subject.trim().length > 0 && customerEmail.trim().length > 0 && (Boolean(isKnown) || emailLooksValid);

  const submit = () => {
    if (!canSubmit) return;
    const known = selected || knownContacts.find(c => c.email && c.email === customerEmail) || null;
    onCreate({
      subject: subject.trim(),
      customerId: known?._contactId || null,
      customerEmail: known ? known.email : customerEmail.trim(),
      customerPhone: known?.phone || null,
      customerName: customerName.trim(), customerOrg: customerOrg.trim(), channel, priority, category: category.trim(), body,
    });
  };

  // A customer was just created in the CRM (via Backbone) — attach it to the
  // ticket draft, refresh the contact list, and close the card.
  const handleCustomerCreated = (created, fallback) => {
    const email = created?.email || fallback?.email || '';
    const nm = created?.displayName || fallback?.name || '';
    setSelected({ _contactId: created?.id || null, email, phone: created?.phone || fallback?.phone || '', name: nm });
    setCustomerEmail(email);
    setCustomerName(nm);
    setCustomerOrg(created?.company || '');
    setAddCustomerOpen(false);
    setContactPickerOpen(false);
    if (loadContacts) loadContacts();
    if (toast) toast(`Customer "${nm || email}" created.`, 'success', 'check');
  };

  return (
    <Animated.View style={[S.backdrop, S.backdropDim, backdropAnim]}>
      <Pressable style={StyleSheet.absoluteFill} onPress={onCancel} />
      <Animated.View style={[S.modalBox, S.modalBoxPremium, Platform.OS === 'web' ? { width: 760, maxHeight: '92vh' } as any : { width: '96%' }, boxAnim]}>
        {/* Premium accent strip */}
        <View style={S.addCardAccent} />
        {/* Header */}
        <View style={[S.modalHead, S.modalHeadPremium]}>
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: 14, flex: 1 }}>
            <View style={S.modalHeadIcon}>
              <Icon name="Ticket" size={20} color={Colors.accent} stroke={1.9} />
            </View>
            <View style={{ flex: 1 }}>
              <Text style={S.modalTitle}>New ticket</Text>
              <Text style={S.modalSubtitle}>Open a ticket on behalf of a customer — it’s assigned a number and routed automatically.</Text>
            </View>
          </View>
          <TouchableOpacity onPress={onCancel} style={S.iconBtn}><Icon name="X" size={15} color={Colors.ink2} /></TouchableOpacity>
        </View>
        {/* Body */}
        <ScrollView style={{ flex: 1 }} contentContainerStyle={S.modalBody}>
          {/* Subject */}
          <View style={S.field}>
            <FieldLabel icon="Edit" required>Subject</FieldLabel>
            <TextInput value={subject} onChangeText={setSubject}
              placeholder="Short summary — e.g. Cannot log in to dashboard"
              placeholderTextColor={Colors.ink4} style={[S.input, { height: 38 }]} autoFocus />
          </View>

          {/* Customer */}
          {/* zIndex lifts this field (and its absolute suggestions dropdown)
              above the Channel/Priority row below, so the dropdown overlays
              cleanly instead of being painted behind it. */}
          <View style={[S.field, { zIndex: contactPickerOpen ? 30 : 1 }]}>
            <FieldLabel icon="User" required>Customer</FieldLabel>
            <View style={{ position: 'relative', zIndex: 30 }}>
              <TextInput
                value={customerEmail}
                onChangeText={(v) => { setCustomerEmail(v); setSelected(null); setContactPickerOpen(true); }}
                onFocus={() => setContactPickerOpen(true)}
                placeholder="Search by email or name · or type a new email"
                placeholderTextColor={Colors.ink4}
                style={[S.input, S.inputMono, { height: 38 }]}
                keyboardType="email-address"
                autoCapitalize="none"
              />
              {/* Suggestions overlay — only when there ARE matching contacts to
                  pick. For a brand-new email we don't overlay anything, so the
                  Name/Company fields below stay visible. */}
              {contactPickerOpen && filtered.length > 0 && (
                <View style={S.dropdown}>
                  <ScrollView
                    style={{ maxHeight: 264 }}
                    showsVerticalScrollIndicator
                    nestedScrollEnabled
                    keyboardShouldPersistTaps="handled"
                  >
                    {filtered.map(c => (
                      <TouchableOpacity key={c._contactId || c.email || c.phone || c.name} onPress={() => pick(c)} style={S.dropdownRow}>
                        <Avatar initials={c.initials} color={c.color} size="sm" />
                        <View style={{ flex: 1, minWidth: 0 }}>
                          <Text style={{ fontSize: 13, fontWeight: FontWeight.semibold, color: Colors.ink }} numberOfLines={1}>{c.name}</Text>
                          <Text style={{ fontSize: 11.5, color: Colors.ink3 }} numberOfLines={1}>{c.email || c.phone}</Text>
                        </View>
                        {c.org && c.org !== '—' && <Text style={{ fontSize: 11.5, color: Colors.ink4 }} numberOfLines={1}>{c.org}</Text>}
                      </TouchableOpacity>
                    ))}
                  </ScrollView>
                  <View style={S.dropdownFoot}>
                    <Text style={{ fontSize: 11, color: Colors.ink4 }}>{filtered.length} contact{filtered.length === 1 ? '' : 's'}</Text>
                    <PrimaryButton size="xs" variant="ghost" onPress={() => setContactPickerOpen(false)}>Close</PrimaryButton>
                  </View>
                </View>
              )}
            </View>
            {/* No match found → offer to create the customer in the CRM. */}
            {!addCustomerOpen && !isKnown && customerEmail.trim() && filtered.length === 0 && !customerName.trim() ? (
              <View style={S.noMatchRow}>
                <View style={{ flex: 1, minWidth: 0 }}>
                  <Text style={S.noMatchTitle}>No matching customer</Text>
                  <Text style={S.noMatchSub} numberOfLines={1}>
                    “{customerEmail.trim()}” isn’t in your CRM yet.
                  </Text>
                </View>
                <PrimaryButton size="sm" variant="primary"
                  leftIcon={<Icon name="Plus" size={13} color="#fff" />}
                  onPress={() => setAddCustomerOpen(true)}>Add customer</PrimaryButton>
              </View>
            ) : null}

            {/* Selected new customer — confirmation chip. */}
            {!addCustomerOpen && customerName.trim() && (selected || (!isKnown && emailLooksValid)) ? (
              <View style={S.selectedCustomerRow}>
                <View style={S.selectedCustomerTick}>
                  <Icon name="Check" size={11} color="#fff" />
                </View>
                <Text style={S.selectedCustomerText} numberOfLines={1}>
                  <Text style={{ fontWeight: FontWeight.semibold, color: Colors.ink }}>{customerName.trim()}</Text>
                  {`  ·  ${selected ? (selected.email || selected.phone) : customerEmail.trim()}`}
                </Text>
                <TouchableOpacity onPress={() => { setCustomerName(''); setCustomerOrg(''); setSelected(null); }} style={S.iconBtn}>
                  <Icon name="X" size={12} color={Colors.ink3} />
                </TouchableOpacity>
              </View>
            ) : null}

            {/* Add-customer card — creates the contact on SK-CRM via Backbone. */}
            {addCustomerOpen ? (
              <AddCustomerCard
                initialName={customerName}
                initialEmail={emailLooksValid ? customerEmail.trim() : ''}
                onClose={() => setAddCustomerOpen(false)}
                onCreated={handleCustomerCreated}
              />
            ) : null}
          </View>

          {/* Channel + Priority + Category — wraps to stacked on narrow screens */}
          <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 16 }}>
            <View style={{ flex: 1, minWidth: 200 }}>
              <FieldLabel icon="Globe">Channel</FieldLabel>
              <SegmentedPicker value={channel} onChange={setChannel} options={[
                { v: 'email', label: 'Email' },
                { v: 'web',   label: 'Web' },
                { v: 'api',   label: 'API' },
                { v: 'agent', label: 'Agent' },
              ]} />
            </View>
            <View style={{ flex: 1, minWidth: 200 }}>
              <FieldLabel icon="Bolt">Priority</FieldLabel>
              <SegmentedPicker value={priority} onChange={setPriority} options={[
                { v: 'low',    label: 'Low',    color: Colors.ink3 },
                { v: 'medium', label: 'Med',    color: Colors.warn },
                { v: 'high',   label: 'High',   color: Colors.danger },
                { v: 'urgent', label: 'Urgent', color: Colors.danger },
              ]} />
            </View>
            <View style={{ flex: 1, minWidth: 200 }}>
              <FieldLabel icon="Tag">Category</FieldLabel>
              <TextInput value={category} onChangeText={setCategory} placeholder="e.g. Billing"
                placeholderTextColor={Colors.ink4} style={[S.input, { height: 38, fontSize: 13 }]} />
            </View>
          </View>

          {/* Body */}
          <View style={S.field}>
            <FieldLabel icon="Reply" hint="(optional)">Initial message</FieldLabel>
            <TextInput value={body} onChangeText={setBody}
              placeholder="Paste the customer's first message, or notes you'd like to attach."
              placeholderTextColor={Colors.ink4}
              multiline style={[S.input, { minHeight: 100, fontSize: 13, paddingTop: 10 }]} />
          </View>
        </ScrollView>
        {/* Footer */}
        <View style={[S.modalFoot, { justifyContent: 'flex-end' }]}>
          <View style={{ flexDirection: 'row', gap: 8 }}>
            <PrimaryButton size="md" variant="default" onPress={onCancel}>Cancel</PrimaryButton>
            <PrimaryButton size="md" variant="primary" leftIcon={<Icon name="Check" size={13} color="#fff" />}
              onPress={submit} disabled={!canSubmit}>Create ticket</PrimaryButton>
          </View>
        </View>
      </Animated.View>
    </Animated.View>
  );
}

// ─────────── FiltersDrawer ───────────
export function FiltersDrawer({ open, onClose, filters, onChange, onClear }) {
  if (!open) return null;
  const { user: authUser } = useAuth();
  const set = (k, v) => onChange({ ...filters, [k]: v });
  return (
    <View style={S.drawerBackdrop}>
      <Pressable style={StyleSheet.absoluteFill} onPress={onClose} />
      <View style={S.drawer}>
        <View style={S.modalHead}>
          <Text style={[S.modalTitle, { fontSize: 16 }]}>Filters</Text>
          <TouchableOpacity onPress={onClose} style={S.iconBtn}><Icon name="X" size={14} color={Colors.ink2} /></TouchableOpacity>
        </View>
        <ScrollView style={{ flex: 1 }} contentContainerStyle={{ padding: 18, gap: 22 }}>
          <FilterGroup label="PRIORITY">
            <CheckboxRow value={filters.priority || []} onChange={(v) => set('priority', v)}
              options={[['urgent','Urgent'],['high','High'],['medium','Medium'],['low','Low']]} />
          </FilterGroup>
          <FilterGroup label="ASSIGNEE">
            <CheckboxRow value={filters.assignee || []} onChange={(v) => set('assignee', v)}
              options={[['me', `${authUser?.first_name || 'Me'} (me)`], ['team','Anyone on my team'], ['unassigned','Unassigned']]} />
          </FilterGroup>
          <FilterGroup label="EMAIL DELIVERY">
            <CheckboxRow value={filters.delivery || []} onChange={(v) => set('delivery', v)}
              options={[['bounce','Has a bounce or partial delivery'],['sent','All emails delivered']]} />
          </FilterGroup>
          <FilterGroup label="INBOX STATE">
            <CheckboxRow value={filters.inbox || []} onChange={(v) => set('inbox', v)}
              options={[['waiting','Waiting on us'],['waitcust','Waiting on customer'],['unread','Unread agent activity']]} />
          </FilterGroup>
          <FilterGroup label="OPENED">
            <CheckboxRow value={filters.opened || []} onChange={(v) => set('opened', v)}
              options={[['today','Today'],['week','This week'],['month','This month'],['older','Older']]} />
          </FilterGroup>
          <FilterGroup label="TAGS">
            <TagInput tags={filters.tags || []} onChange={(v) => set('tags', v)} placeholder="invoice, refund…" />
          </FilterGroup>
        </ScrollView>
        <View style={[S.modalFoot, { justifyContent: 'space-between' }]}>
          <PrimaryButton size="md" variant="ghost" onPress={onClear}>Clear all</PrimaryButton>
          <PrimaryButton size="md" variant="primary" leftIcon={<Icon name="Check" size={13} color="#fff" />} onPress={onClose}>Apply</PrimaryButton>
        </View>
      </View>
    </View>
  );
}

// ─────────── NewContactModal ───────────
export function NewContactModal({ open, onCancel, onCreate }) {
  const [name, setName] = useState('');
  const [nameError, setNameError] = useState('');
  const [country, setCountry] = useState<CountryEntry>(DEFAULT_COUNTRY);
  const [mobile, setMobile] = useState('');
  const [mobileTouched, setMobileTouched] = useState(false);
  const [email, setEmail] = useState('');
  const [emailTouched, setEmailTouched] = useState(false);
  const [company, setCompany] = useState('');
  const [companyError, setCompanyError] = useState('');
  const [tags, setTags] = useState([]);

  useEffect(() => {
    if (!open) {
      setName(''); setNameError(''); setCountry(DEFAULT_COUNTRY); setMobile(''); setMobileTouched(false);
      setEmail(''); setEmailTouched(false); setCompany(''); setCompanyError(''); setTags([]);
    }
  }, [open]);

  // Entrance: dimmed backdrop + the card lifts and scales in (portrait focus).
  const enter = useRef(new Animated.Value(0)).current;
  useEffect(() => {
    if (open) {
      enter.setValue(0);
      Animated.timing(enter, { toValue: 1, duration: 300, easing: Easing.out(Easing.cubic), useNativeDriver: Platform.OS !== 'web' }).start();
    }
  }, [open]);

  if (!open) return null;

  const backdropAnim = { opacity: enter };
  const boxAnim = {
    opacity: enter,
    transform: [
      { translateY: enter.interpolate({ inputRange: [0, 1], outputRange: [18, 0] }) },
      { scale: enter.interpolate({ inputRange: [0, 1], outputRange: [0.97, 1] }) },
    ],
  };

  // Full name — letters & spaces only.
  const handleNameChange = (t: string) => {
    const bad = t.match(/[^A-Za-z ]/);
    if (bad) {
      setNameError(/[0-9]/.test(bad[0]) ? 'Numbers are not allowed.' : `Special character "${bad[0]}" is not allowed.`);
    } else {
      setNameError('');
    }
    setName(t.replace(/[^A-Za-z ]/g, ''));
  };
  const nameOk = name.trim().length > 0 && !nameError;

  // Company — letters, digits, spaces and a few real business marks (& . , - ' / ( )).
  // Anything else (@ # $ % * …) is rejected (and stripped) with a message.
  const handleCompanyChange = (t: string) => {
    const bad = t.match(/[^A-Za-z0-9 &.,'/()-]/);
    setCompanyError(bad ? `Special character "${bad[0]}" is not allowed.` : '');
    setCompany(t.replace(/[^A-Za-z0-9 &.,'/()-]/g, ''));
  };

  // Phone — required; must be a complete number for the selected country.
  const mobileDigits = mobile.replace(/\D/g, '');
  const phoneComplete = mobileDigits.length === country.digitCount;
  const phone = phoneComplete ? `${country.dialCode}${mobileDigits}` : null;
  const mobileError = mobileTouched && !phoneComplete
    ? (mobileDigits.length === 0 ? 'Phone is required.' : `Enter ${country.digitCount} digits for ${country.name}.`)
    : undefined;

  // Email — required; only the allowed charset + standard format.
  const emailTrim = email.trim();
  const emailBadChar = email.match(/[^A-Za-z0-9._+@-]/);
  const emailFormatOk = /^[A-Za-z0-9._+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/.test(emailTrim);
  const emailOk = !emailBadChar && emailFormatOk;
  const emailError = emailBadChar
    ? `Character "${emailBadChar[0]}" is not allowed.`
    : (emailTouched ? (!emailTrim ? 'Email is required.' : (!emailFormatOk ? 'Enter a valid email address.' : undefined)) : undefined);

  // A contact needs a name, a phone AND an email.
  const canSubmit = nameOk && phoneComplete && emailOk;

  const submit = () => {
    if (!canSubmit) return;
    onCreate({
      name: name.trim(),
      phones: phone ? [phone] : [],
      emails: emailOk ? [emailTrim.toLowerCase()] : [],
      company: company.trim(),
      tags,
    });
  };

  return (
    <Animated.View style={[S.backdrop, S.backdropDim, backdropAnim]}>
      <Pressable style={StyleSheet.absoluteFill} onPress={onCancel} />
      <Animated.View style={[S.modalBox, S.modalBoxPremium, Platform.OS === 'web' ? { width: 720, maxHeight: '92vh' } as any : { width: '96%' }, boxAnim]}>
        {/* Premium accent strip */}
        <View style={S.addCardAccent} />
        <View style={[S.modalHead, S.modalHeadPremium]}>
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: 14, flex: 1 }}>
            <View style={S.modalHeadIcon}>
              <Icon name="User" size={20} color={Colors.accent} stroke={1.9} />
            </View>
            <View style={{ flex: 1 }}>
              <Text style={S.modalTitle}>New contact</Text>
            </View>
          </View>
          <TouchableOpacity onPress={onCancel} style={S.iconBtn}><Icon name="X" size={15} color={Colors.ink2} /></TouchableOpacity>
        </View>
        <ScrollView style={{ flex: 1 }} contentContainerStyle={S.modalBody}>
          {/* Full name */}
          <CommonInput
            dense
            required
            label="Full name"
            placeholder="Enter Contact Name"
            value={name}
            onChangeText={handleNameChange}
            touched={!!nameError}
            error={nameError || undefined}
          />

          {/* Phone + Email */}
          <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 22 }}>
            <View style={{ flex: 1, minWidth: 220 }}>
              <MobileNumberInput
                dense
                required
                label="Phone"
                value={mobile}
                selectedCountry={country}
                onCountryChange={(c) => { setCountry(c); setMobile(''); }}
                onChangeText={setMobile}
                onBlur={() => setMobileTouched(true)}
                placeholder="Enter the mobile number"
                error={mobileError}
              />
            </View>
            <View style={{ flex: 1, minWidth: 220 }}>
              <Email_Input
                dense
                required
                label="Email"
                value={email}
                onValueChange={(val) => { setEmail(val); if (/[^A-Za-z0-9._+@-]/.test(val)) setEmailTouched(true); }}
                onBlur={() => setEmailTouched(true)}
                touched={emailTouched || !!emailBadChar}
                error={emailError || ''}
              />
            </View>
          </View>

          {/* Company + Tags */}
          <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 22 }}>
            <View style={{ flex: 1, minWidth: 220 }}>
              <CommonInput
                dense
                label="Company"
                placeholder='Enter the Company Name'
                value={company}
                onChangeText={handleCompanyChange}
                touched={!!companyError}
                error={companyError || undefined}
              />
            </View>
            <View style={{ flex: 1, minWidth: 220 }}>
              <Text style={[S.fieldLabel, { marginBottom: 6 }]}>Tags</Text>
              <TagInput tags={tags} onChange={setTags} placeholder="vip, repeat…" />
            </View>
          </View>
        </ScrollView>
        <View style={[S.modalFoot, { justifyContent: 'flex-end' }]}>
          <View style={{ flexDirection: 'row', gap: 8 }}>
            <PrimaryButton size="md" variant="default" onPress={onCancel}>Cancel</PrimaryButton>
            <PrimaryButton size="md" variant="primary" leftIcon={<Icon name="Check" size={13} color="#fff" />}
              onPress={submit} disabled={!canSubmit}>Create contact</PrimaryButton>
          </View>
        </View>
      </Animated.View>
    </Animated.View>
  );
}

// ─────────── ExportModal ───────────
export function ExportModal({
  open, onClose, onExport,
  title = 'Export contacts',
  scopes = [{ id: 'filtered', label: 'Filtered view', sub: 'Current filter applied' }, { id: 'all', label: 'All records', sub: 'Everything in the system' }],
  columnGroups = [{ label: 'Core', cols: [{ key: 'name', slug: 'name', label: 'Full name', default: true }, { key: 'email', slug: 'email', label: 'Email', default: true }] }],
  defaultColumns = ['name', 'email'],
  defaultDelivery = 'download',
  totalsByScope = {},
}) {
  const { user: authUser } = useAuth();
  const [scope, setScope] = useState(scopes[0]?.id);
  const [format, setFormat] = useState('csv');
  const [columns, setColumns] = useState(defaultColumns || []);
  const [delivery, setDelivery] = useState(defaultDelivery);

  useEffect(() => {
    if (open) {
      setScope(scopes[0]?.id);
      setFormat('csv');
      setColumns(defaultColumns || []);
      setDelivery(defaultDelivery);
    }
  }, [open]);

  if (!open) return null;

  const t = totalsByScope[scope] || { rows: 0 };
  const approxKb = t.kb != null ? t.kb : Math.max(1, Math.round(t.rows * columns.length * 12 / 1024));

  return (
    <View style={S.backdrop}>
      <Pressable style={StyleSheet.absoluteFill} onPress={onClose} />
      <View style={[S.modalBox, Platform.OS === 'web' ? { width: 820, maxHeight: '94vh' } as any : { width: '98%' }]}>
        <View style={S.modalHead}>
          <Text style={S.modalTitle}>{title}</Text>
          <TouchableOpacity onPress={onClose} style={S.iconBtn}><Icon name="X" size={15} color={Colors.ink2} /></TouchableOpacity>
        </View>
        <ScrollView style={{ flex: 1 }} contentContainerStyle={[S.modalBody, { gap: 0 }]}>
          <Text style={S.sectionLabel}>What to export</Text>
          <View style={{ flexDirection: 'row', gap: 12, marginBottom: 22 }}>
            {scopes.map(s => (
              <PickerCard key={s.id} active={scope === s.id} title={s.label} sub={s.sub}
                disabled={s.disabled} onPress={() => setScope(s.id)} />
            ))}
          </View>
          <Text style={S.sectionLabel}>Format</Text>
          <View style={{ flexDirection: 'row', gap: 12, marginBottom: 22 }}>
            <PickerCard active={format === 'csv'}  title="CSV"  sub="Excel / Sheets-friendly" onPress={() => setFormat('csv')} />
            <PickerCard active={format === 'xlsx'} title="XLSX" sub="Native Excel"            onPress={() => setFormat('xlsx')} />
            <PickerCard active={format === 'json'} title="JSON" sub="Full nested · for ETL"   onPress={() => setFormat('json')} />
          </View>
          <ColumnPicker groups={columnGroups} selected={columns} onChange={setColumns} />
          <View style={{ marginTop: 22 }}>
            <Text style={S.sectionLabel}>Delivery</Text>
            <View style={{ flexDirection: 'row', gap: 12 }}>
              <PickerCard active={delivery === 'download'} title="Download now"  sub="Browser download"      onPress={() => setDelivery('download')} />
              <PickerCard active={delivery === 'email'}    title="Email to me"   sub={authUser?.email || 'your email'}  onPress={() => setDelivery('email')} />
              <PickerCard active={delivery === 'schedule'} title="Scheduled"     sub="Recurring · Phase 4"   onPress={() => setDelivery('schedule')} disabled />
            </View>
          </View>
          {/* DPDP notice */}
          <View style={[S.infoBox, { marginTop: 20 }]}>
            <Icon name="Lock" size={16} color={Colors.ink2} />
            <View style={{ flex: 1 }}>
              <Text style={{ fontSize: 13.5, fontWeight: FontWeight.semibold, color: Colors.ink }}>DPDP & audit</Text>
              <Text style={{ fontSize: 12.5, color: Colors.ink3, marginTop: 3, lineHeight: 18 }}>
                Export is logged with your name, scope, and column set. Files contain PII — handle accordingly.
              </Text>
            </View>
          </View>
        </ScrollView>
        <View style={S.modalFoot}>
          <Text style={S.footMono}>{t.rows} rows · {columns.length} columns · ~{approxKb} KB</Text>
          <View style={{ flexDirection: 'row', gap: 8 }}>
            <PrimaryButton size="md" variant="default" onPress={onClose}>Cancel</PrimaryButton>
            <PrimaryButton size="md" variant="primary" leftIcon={<Icon name="ArrowRight" size={13} color="#fff" />}
              onPress={() => onExport && onExport({ scope, format, columns, delivery })}
              disabled={columns.length === 0}>
              Generate export
            </PrimaryButton>
          </View>
        </View>
      </View>
    </View>
  );
}

// ─────────── NewTemplateModal ───────────
const TEMPLATE_EVENTS = [
  { value: 'ticket.created',  label: 'Ticket created',       hint: 'Sent to the customer when a new ticket is opened' },
  { value: 'agent.reply',     label: 'Agent reply',          hint: 'Sent on every public agent comment' },
  { value: 'ticket.resolved', label: 'Ticket resolved',      hint: 'Sent when status changes to resolved' },
  { value: 'ticket.closed',   label: 'Ticket closed',        hint: 'Sent when status changes to closed' },
  { value: 'csat.request',    label: 'CSAT request',         hint: 'Sent after a configurable delay to collect rating' },
  { value: 'ticket.assigned', label: 'Ticket assigned',      hint: 'Sent to an agent when assigned a ticket (internal)' },
  { value: 'custom',          label: 'Custom (manual send)', hint: 'Triggered manually or by an automation rule' },
];

const STARTER_SUBJECTS = {
  blank: '',
  basic: '[TKT-{{ticket_number}}] {{ticket_subject}}',
  reply: '[TKT-{{ticket_number}}] Re: {{ticket_subject}}',
  csat:  'How did we do, {{customer_name}}?',
};

export function NewTemplateModal({ open, onCancel, onCreate, existingSlugs = [], locale = 'EN' }) {
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [slugTouched, setSlugTouched] = useState(false);
  const [event, setEvent] = useState('custom');
  const [starter, setStarter] = useState('basic');

  useEffect(() => {
    if (!slugTouched) setSlug(name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, ''));
  }, [name, slugTouched]);

  if (!open) return null;

  const slugError = !slug ? null
    : !/^[a-z][a-z0-9-]*$/.test(slug) ? 'Lowercase letters, digits and dashes only.'
    : existingSlugs.includes(slug) ? 'A template with this slug already exists.'
    : null;
  const canSubmit = name.trim() && slug && !slugError;

  const submit = () => {
    if (!canSubmit) return;
    const meta = TEMPLATE_EVENTS.find(e => e.value === event);
    onCreate({ name: name.trim(), slug, event, desc: meta?.hint || 'Custom template.', locale });
  };

  return (
    <View style={S.backdrop}>
      <Pressable style={StyleSheet.absoluteFill} onPress={onCancel} />
      <View style={[S.modalBox, Platform.OS === 'web' ? { width: 680, maxHeight: '92vh' } as any : { width: '96%' }]}>
        <View style={S.modalHead}>
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: 12 }}>
            <View style={S.iconPill}>
              <Icon name="Paper" size={16} color={Colors.accentInk} />
            </View>
            <View>
              <Text style={S.modalTitle}>New email template</Text>
              <Text style={{ fontSize: 11.5, color: Colors.ink3, marginTop: 2 }}>You'll be taken to the editor after creation.</Text>
            </View>
          </View>
          <TouchableOpacity onPress={onCancel} style={S.iconBtn}><Icon name="X" size={14} color={Colors.ink2} /></TouchableOpacity>
        </View>
        <ScrollView style={{ flex: 1 }} contentContainerStyle={[S.modalBody, { gap: 16 }]}>
          {/* Name + Slug */}
          <View style={{ flexDirection: 'row', gap: 14 }}>
            <View style={[S.field, { flex: 1 }]}>
              <View style={{ flexDirection: 'row' }}>
                <Text style={S.fieldLabel}>Template name</Text><RequiredStar />
              </View>
              <TextInput value={name} onChangeText={setName} placeholder="e.g. Refund processed"
                placeholderTextColor={Colors.ink4} style={S.input} autoFocus />
            </View>
            <View style={[S.field, { flex: 1 }]}>
              <View style={{ flexDirection: 'row' }}>
                <Text style={S.fieldLabel}>Slug</Text><RequiredStar />
              </View>
              <TextInput value={slug}
                onChangeText={(v) => { setSlugTouched(true); setSlug(v.toLowerCase()); }}
                placeholder="refund-processed" placeholderTextColor={Colors.ink4}
                style={[S.input, S.inputMono]} />
              {slugError
                ? <Text style={S.fieldError}>{slugError}</Text>
                : <Text style={S.fieldHint}>Used in the API. Auto-derived from name.</Text>}
            </View>
          </View>

          {/* Triggered by */}
          <View style={S.field}>
            <Text style={S.fieldLabel}>Triggered by</Text>
            <View style={S.selectBox}>
              {TEMPLATE_EVENTS.map(e => (
                <TouchableOpacity key={e.value} onPress={() => setEvent(e.value)}
                  style={[S.selectOption, event === e.value && S.selectOptionActive]}>
                  <Text style={[S.selectOptionText, event === e.value && { color: Colors.ink, fontWeight: FontWeight.semibold }]}>{e.label}</Text>
                </TouchableOpacity>
              ))}
            </View>
            <Text style={S.fieldHint}>{TEMPLATE_EVENTS.find(e => e.value === event)?.hint}</Text>
          </View>

          {/* Start from */}
          <View>
            <Text style={[S.fieldLabel, { marginBottom: 8 }]}>Start from</Text>
            <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 10 }}>
              <PickerCard active={starter === 'blank'} title="Blank"
                sub="Just a greeting and sign-off — fill in the rest." onPress={() => setStarter('blank')} />
              <PickerCard active={starter === 'basic'} title="Basic notification"
                sub="Subject + heading + link to ticket. Most flexible." onPress={() => setStarter('basic')} />
              <PickerCard active={starter === 'reply'} title="Agent reply"
                sub="Embeds the agent's comment in a blockquote." onPress={() => setStarter('reply')} />
              <PickerCard active={starter === 'csat'} title="CSAT request"
                sub="Includes a {{csat_url}} link to leave a rating." onPress={() => setStarter('csat')} />
            </View>
          </View>

          {/* Preview subject */}
          <View style={S.previewBox}>
            <Text style={S.previewLabel}>PREVIEW SUBJECT</Text>
            <Text style={[S.inputMono, { fontSize: 12, color: Colors.ink }]}>{STARTER_SUBJECTS[starter] || '(empty)'}</Text>
          </View>
        </ScrollView>
        <View style={[S.modalFoot, { justifyContent: 'flex-end' }]}>
          <View style={{ flexDirection: 'row', gap: 8 }}>
            <PrimaryButton size="md" variant="default" onPress={onCancel}>Cancel</PrimaryButton>
            <PrimaryButton size="md" variant="primary" leftIcon={<Icon name="ArrowRight" size={13} color="#fff" />}
              onPress={submit} disabled={!canSubmit}>Create and edit</PrimaryButton>
          </View>
        </View>
      </View>
    </View>
  );
}

// ─────────── RestoreDialog ───────────
export function RestoreDialog({ open, templateName, onCancel, onConfirm }) {
  if (!open) return null;
  return (
    <View style={S.backdrop}>
      <Pressable style={StyleSheet.absoluteFill} onPress={onCancel} />
      <View style={[S.modalBox, Platform.OS === 'web' ? { width: 480 } as any : { width: '90%' }]}>
        <View style={S.modalHead}>
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: 10 }}>
            <View style={[S.iconPill, { backgroundColor: Colors.dangerSoft }]}>
              <Icon name="Refresh" size={14} color={Colors.danger} />
            </View>
            <Text style={S.modalTitle}>Restore default template?</Text>
          </View>
          <TouchableOpacity onPress={onCancel} style={S.iconBtn}><Icon name="X" size={14} color={Colors.ink2} /></TouchableOpacity>
        </View>
        <View style={[S.modalBody, { gap: 0 }]}>
          <Text style={{ fontSize: 13.5, color: Colors.ink2, lineHeight: 21 }}>
            This will replace your customised <Text style={{ fontWeight: FontWeight.semibold, color: Colors.ink }}>"{templateName}"</Text> template with the Softknack default. This cannot be undone.
          </Text>
          <View style={[S.infoBox, { marginTop: 14 }]}>
            <Icon name="Alert" size={14} color={Colors.warn} />
            <Text style={{ flex: 1, fontSize: 12.5, color: Colors.ink3, lineHeight: 18 }}>
              Other locales of this template stay customised. Only the <Text style={{ fontWeight: FontWeight.semibold, color: Colors.ink2 }}>English</Text> version is affected.
            </Text>
          </View>
        </View>
        <View style={[S.modalFoot, { justifyContent: 'flex-end' }]}>
          <View style={{ flexDirection: 'row', gap: 8 }}>
            <PrimaryButton size="md" variant="ghost" onPress={onCancel}>Cancel</PrimaryButton>
            <PrimaryButton size="md" variant="danger" leftIcon={<Icon name="Refresh" size={13} color="#fff" />} onPress={onConfirm}>Restore default</PrimaryButton>
          </View>
        </View>
      </View>
    </View>
  );
}

// ─────────── TemplatePreviewModal ───────────
function fillVars(text, vars) {
  return (text || '').replace(/\{\{(\w+)\}\}/g, (_, k) => vars[k] != null ? vars[k] : `{{${k}}}`);
}

export function TemplatePreviewModal({ open, template, onClose }) {
  if (!open || !template) return null;
  const { user: authUser, org: authOrg } = useAuth();
  const { channel } = useApp();
  const PREVIEW_VARS = {
    ticket_number: '1041',
    ticket_subject: 'Billing discrepancy on January invoice',
    ticket_url: 'https://support.softknack.io/t/1041',
    agent_name: authUser ? `${authUser.first_name} ${authUser.last_name}`.trim() : 'Agent Name',
    customer_name: 'Customer Name',
    org_name: authOrg?.name || 'Your Company',
    comment_body: 'Hi, I have reviewed your request.',
    csat_url: 'https://support.softknack.io/c/abc123',
    support_email: channel?.senderEmail || 'support@yourcompany.com',
  };
  const filledSubject = fillVars(template.subject || '', PREVIEW_VARS);
  const filledBody = fillVars(template.body || '', PREVIEW_VARS);

  return (
    <View style={S.backdrop}>
      <Pressable style={StyleSheet.absoluteFill} onPress={onClose} />
      <View style={[S.modalBox, Platform.OS === 'web' ? { width: 720, maxHeight: '90vh' } as any : { width: '96%' }]}>
        <View style={S.modalHead}>
          <View>
            <Text style={S.modalTitle}>Preview · HTML</Text>
            <Text style={{ fontSize: 11.5, color: Colors.ink3, marginTop: 2 }}>Rendered with sample variable values.</Text>
          </View>
          <TouchableOpacity onPress={onClose} style={S.iconBtn}><Icon name="X" size={14} color={Colors.ink2} /></TouchableOpacity>
        </View>
        {/* Subject bar */}
        <View style={{ padding: '16px 22px' as any, borderBottomWidth: 1, borderBottomColor: Colors.line, backgroundColor: Colors.paper2 }}>
          <Text style={{ fontSize: 11, fontWeight: FontWeight.bold, color: Colors.ink4, letterSpacing: 0.8, marginBottom: 4 }}>SUBJECT</Text>
          <Text style={{ fontSize: 15, fontWeight: FontWeight.semibold, color: Colors.ink }}>{filledSubject || '(empty)'}</Text>
          <View style={{ flexDirection: 'row', gap: 8, marginTop: 8 }}>
            <Text style={{ fontSize: 12, color: Colors.ink3 }}><Text style={{ fontWeight: FontWeight.semibold, color: Colors.ink2 }}>From:</Text> {channel?.senderName || 'Support'} &lt;{channel?.senderEmail || 'support@yourcompany.com'}&gt;</Text>
            <Text style={{ flex: 1 }} />
            <Text style={{ fontSize: 12, color: Colors.ink3 }}><Text style={{ fontWeight: FontWeight.semibold, color: Colors.ink2 }}>To:</Text> {PREVIEW_VARS.customer_name} &lt;customer@example.com&gt;</Text>
          </View>
        </View>
        <ScrollView style={{ flex: 1 }} contentContainerStyle={{ padding: 22, backgroundColor: '#f3f3f3' }}>
          <View style={{ backgroundColor: '#fff', maxWidth: 600, alignSelf: 'center', padding: 32,
            borderWidth: 1, borderColor: Colors.line, borderRadius: Radii.r3 }}>
            <Text style={{ fontFamily: Platform.OS === 'web' ? '"IBM Plex Sans", sans-serif' : undefined,
              fontSize: 14, color: '#111', lineHeight: 21 }}>{filledBody}</Text>
          </View>
        </ScrollView>
        <View style={[S.modalFoot, { justifyContent: 'flex-end' }]}>
          <PrimaryButton size="md" variant="default" onPress={onClose}>Close</PrimaryButton>
        </View>
      </View>
    </View>
  );
}

// ─────────── FieldEditor ───────────
export function FieldEditor({ open, field = {}, onCancel, onSave, existingNames = [] }) {
  const [label, setLabel] = useState(field.label || '');
  const [name, setName] = useState(field.name || '');
  const [type, setType] = useState(field.type || 'Text');
  const [required, setRequired] = useState(!!field.required);
  const [description, setDescription] = useState(field.description || '');
  const [placeholder, setPlaceholder] = useState(field.placeholder || '');
  const [options, setOptions] = useState(field.options || []);
  const [defaultValue, setDefaultValue] = useState(field.defaultValue || '');
  const [newOption, setNewOption] = useState('');
  const [nameTouched, setNameTouched] = useState(!field._isNew);

  useEffect(() => {
    if (!nameTouched) setName(label.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, ''));
  }, [label, nameTouched]);

  useEffect(() => {
    if (field) {
      setLabel(field.label || ''); setName(field.name || ''); setType(field.type || 'Text');
      setRequired(!!field.required); setDescription(field.description || ''); setPlaceholder(field.placeholder || '');
      setOptions(field.options || []); setDefaultValue(field.defaultValue || '');
      setNameTouched(!field._isNew);
    }
  }, [field?.id]);

  if (!open) return null;

  const nameError = !name ? null
    : !/^[a-z][a-z0-9_]*$/.test(name) ? 'Lowercase letters, digits and underscores only.'
    : existingNames.includes(name) ? 'A field with this slug already exists.'
    : null;
  const canSubmit = label.trim() && name && !nameError && (type !== 'Select' || options.length > 0);

  const addOption = () => {
    const v = newOption.trim();
    if (!v || options.includes(v)) return;
    setOptions(o => [...o, v]);
    setNewOption('');
  };

  const FIELD_TYPES = ['Text','Number','Date','Select','Boolean','URL','Email'];

  return (
    <View style={S.backdrop}>
      <Pressable style={StyleSheet.absoluteFill} onPress={onCancel} />
      <View style={[S.modalBox, Platform.OS === 'web' ? { width: 640, maxHeight: '92vh' } as any : { width: '96%' }]}>
        <View style={S.modalHead}>
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: 12 }}>
            <View style={S.iconPill}>
              <Icon name="Tag" size={16} color={Colors.accentInk} />
            </View>
            <View>
              <Text style={S.modalTitle}>{field._isNew ? 'New custom field' : 'Edit field'}</Text>
              <Text style={{ fontSize: 11.5, color: Colors.ink3, marginTop: 2 }}>Captured on every ticket. Surfaced in the right panel and the API.</Text>
            </View>
          </View>
          <TouchableOpacity onPress={onCancel} style={S.iconBtn}><Icon name="X" size={14} color={Colors.ink2} /></TouchableOpacity>
        </View>
        <ScrollView style={{ flex: 1 }} contentContainerStyle={[S.modalBody, { gap: 16 }]}>
          {/* Label + Slug */}
          <View style={{ flexDirection: 'row', gap: 14 }}>
            <View style={[S.field, { flex: 1 }]}>
              <View style={{ flexDirection: 'row' }}>
                <Text style={S.fieldLabel}>Display label</Text><RequiredStar />
              </View>
              <TextInput value={label} onChangeText={setLabel} placeholder="e.g. Order number"
                placeholderTextColor={Colors.ink4} style={S.input} autoFocus />
              <Text style={S.fieldHint}>Shown to agents on the ticket.</Text>
            </View>
            <View style={[S.field, { flex: 1 }]}>
              <View style={{ flexDirection: 'row' }}>
                <Text style={S.fieldLabel}>API slug</Text><RequiredStar />
              </View>
              <TextInput value={name}
                onChangeText={(v) => { setNameTouched(true); setName(v.toLowerCase()); }}
                placeholder="order_number" placeholderTextColor={Colors.ink4}
                style={[S.input, S.inputMono]}
                editable={field.source !== 'system'} />
              {nameError
                ? <Text style={S.fieldError}>{nameError}</Text>
                : <Text style={S.fieldHint}>Used in the API. Auto-derived from the label.</Text>}
            </View>
          </View>

          {/* Type + Required */}
          <View style={{ flexDirection: 'row', gap: 14, alignItems: 'flex-end' }}>
            <View style={[S.field, { flex: 1 }]}>
              <Text style={S.fieldLabel}>Type</Text>
              <View style={S.selectBox}>
                {FIELD_TYPES.map(t => (
                  <TouchableOpacity key={t} onPress={() => field.source !== 'system' && setType(t)}
                    style={[S.selectOption, type === t && S.selectOptionActive]}>
                    <Text style={[S.selectOptionText, type === t && { color: Colors.ink, fontWeight: FontWeight.semibold }]}>{t}</Text>
                  </TouchableOpacity>
                ))}
              </View>
            </View>
            <TouchableOpacity onPress={() => setRequired(v => !v)}
              style={[S.toggleRow, { marginBottom: 2 }]}>
              <Toggle on={required} onPress={() => setRequired(v => !v)} />
              <Text style={{ fontSize: 13, color: Colors.ink2 }}>Required to create a ticket</Text>
            </TouchableOpacity>
          </View>

          {/* Description */}
          <View style={S.field}>
            <Text style={S.fieldLabel}>Helper text <Text style={{ color: Colors.ink3, fontWeight: FontWeight.regular }}>(optional)</Text></Text>
            <TextInput value={description} onChangeText={setDescription}
              placeholder="A short note explaining what this field is for"
              placeholderTextColor={Colors.ink4} style={S.input} />
          </View>

          {/* Placeholder */}
          {type !== 'Boolean' && type !== 'Select' && (
            <View style={S.field}>
              <Text style={S.fieldLabel}>Placeholder <Text style={{ color: Colors.ink3, fontWeight: FontWeight.regular }}>(optional)</Text></Text>
              <TextInput value={placeholder} onChangeText={setPlaceholder}
                placeholder={type === 'Number' ? '0' : type === 'Date' ? 'YYYY-MM-DD' : 'Example value'}
                placeholderTextColor={Colors.ink4} style={S.input} />
            </View>
          )}

          {/* Options */}
          {type === 'Select' && (
            <View>
              <View style={{ flexDirection: 'row' }}>
                <Text style={[S.fieldLabel, { marginBottom: 6 }]}>Options</Text><RequiredStar />
              </View>
              <View style={S.optionsBox}>
                {options.map((o, i) => (
                  <View key={o} style={S.optionRow}>
                    <Text style={S.optionIdx}>{String(i+1).padStart(2,'0')}</Text>
                    <Text style={{ flex: 1, fontSize: 13, color: Colors.ink }}>{o}</Text>
                    <TouchableOpacity onPress={() => setOptions(os => os.filter((_,j) => j !== i))} style={S.iconBtn}>
                      <Icon name="X" size={11} color={Colors.ink3} />
                    </TouchableOpacity>
                  </View>
                ))}
                <View style={{ flexDirection: 'row', gap: 6, marginTop: options.length ? 6 : 0 }}>
                  <TextInput value={newOption} onChangeText={setNewOption}
                    placeholder="Add an option, press Enter" placeholderTextColor={Colors.ink4}
                    onSubmitEditing={addOption}
                    style={[S.input, { flex: 1, height: 30, fontSize: 12 }]} />
                  <PrimaryButton size="sm" variant="default" leftIcon={<Icon name="Plus" size={12} color={Colors.ink} />}
                    onPress={addOption} disabled={!newOption.trim()}>Add</PrimaryButton>
                </View>
              </View>
            </View>
          )}

          {/* Default value */}
          {(type === 'Select' && options.length > 0) && (
            <View style={S.field}>
              <Text style={S.fieldLabel}>Default option <Text style={{ color: Colors.ink3, fontWeight: FontWeight.regular }}>(optional)</Text></Text>
              <View style={S.selectBox}>
                {['', ...options].map(o => (
                  <TouchableOpacity key={o || '_none'} onPress={() => setDefaultValue(o)}
                    style={[S.selectOption, defaultValue === o && S.selectOptionActive]}>
                    <Text style={[S.selectOptionText, defaultValue === o && { color: Colors.ink, fontWeight: FontWeight.semibold }]}>{o || 'No default'}</Text>
                  </TouchableOpacity>
                ))}
              </View>
            </View>
          )}

          {type === 'Boolean' && (
            <View style={S.field}>
              <Text style={S.fieldLabel}>Default value</Text>
              <View style={{ flexDirection: 'row', gap: 8 }}>
                {[['false','Off (false)'],['true','On (true)']].map(([v,l]) => (
                  <TouchableOpacity key={v} onPress={() => setDefaultValue(v)}
                    style={[S.selectOption, defaultValue === v && S.selectOptionActive]}>
                    <Text style={[S.selectOptionText, defaultValue === v && { color: Colors.ink, fontWeight: FontWeight.semibold }]}>{l}</Text>
                  </TouchableOpacity>
                ))}
              </View>
            </View>
          )}

          {/* Live preview */}
          <View>
            <Text style={S.previewLabel}>PREVIEW</Text>
            <View style={[S.previewBox, { marginTop: 6 }]}>
              <FieldPreview label={label || '(label)'} required={required} type={type}
                placeholder={placeholder} options={options} defaultValue={defaultValue} description={description} />
            </View>
          </View>
        </ScrollView>
        <View style={[S.modalFoot, { justifyContent: 'flex-end' }]}>
          <View style={{ flexDirection: 'row', gap: 8 }}>
            <PrimaryButton size="md" variant="default" onPress={onCancel}>Cancel</PrimaryButton>
            <PrimaryButton size="md" variant="primary" leftIcon={<Icon name="Check" size={13} color="#fff" />}
              onPress={() => onSave({ ...field, label: label.trim(), name, type, required, description: description.trim(), placeholder: placeholder.trim(), options, defaultValue })}
              disabled={!canSubmit}>
              {field._isNew ? 'Create field' : 'Save changes'}
            </PrimaryButton>
          </View>
        </View>
      </View>
    </View>
  );
}

// ─────────── InviteAgent ───────────
const ROLE_PERMS = {
  owner:     ['View tickets','Reply on tickets','Resolve / re-open','Edit contacts','Bulk export','Edit templates','Manage agents','Delete workspace'],
  admin:     ['View tickets','Reply on tickets','Resolve / re-open','Edit contacts','Bulk export','Edit templates'],
  agent:     ['View tickets','Reply on tickets','Resolve / re-open','Edit contacts'],
  'read-only': ['View tickets'],
};

export function InviteAgent({ open, onCancel, onInvite }) {
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('agent');
  if (!open) return null;
  const perms = ROLE_PERMS[role] || [];

  return (
    <View style={S.backdrop}>
      <Pressable style={StyleSheet.absoluteFill} onPress={onCancel} />
      <View style={[S.modalBox, Platform.OS === 'web' ? { width: 480 } as any : { width: '92%' }]}>
        <View style={S.modalHead}>
          <Text style={S.modalTitle}>Invite agent</Text>
          <TouchableOpacity onPress={onCancel} style={S.iconBtn}><Icon name="X" size={14} color={Colors.ink2} /></TouchableOpacity>
        </View>
        <View style={[S.modalBody, { gap: 16 }]}>
          <View style={S.field}>
            <Text style={S.fieldLabel}>Email</Text>
            <TextInput value={email} onChangeText={setEmail} placeholder="teammate@acme.com"
              placeholderTextColor={Colors.ink4} keyboardType="email-address"
              style={[S.input, S.inputMono]} autoFocus />
          </View>
          <View style={S.field}>
            <Text style={S.fieldLabel}>Role</Text>
            <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 8 }}>
              {['owner','admin','agent','read-only'].map(r => (
                <TouchableOpacity key={r} onPress={() => setRole(r)}
                  style={[S.selectOption, role === r && S.selectOptionActive, { minWidth: 90 }]}>
                  <Text style={[S.selectOptionText, role === r && { color: Colors.ink, fontWeight: FontWeight.semibold }]}>{r.charAt(0).toUpperCase()+r.slice(1)}</Text>
                </TouchableOpacity>
              ))}
            </View>
          </View>
          {/* Permissions preview */}
          <View style={S.previewBox}>
            <Text style={S.previewLabel}>PERMISSIONS FOR {role.toUpperCase()}</Text>
            <View style={{ marginTop: 6, gap: 4 }}>
              {perms.map(p => (
                <View key={p} style={{ flexDirection: 'row', alignItems: 'center', gap: 6 }}>
                  <Icon name="Check" size={12} color={Colors.success} />
                  <Text style={{ fontSize: 12.5, color: Colors.ink2 }}>{p}</Text>
                </View>
              ))}
            </View>
          </View>
        </View>
        <View style={[S.modalFoot, { justifyContent: 'flex-end' }]}>
          <View style={{ flexDirection: 'row', gap: 8 }}>
            <PrimaryButton size="md" variant="default" onPress={onCancel}>Cancel</PrimaryButton>
            <PrimaryButton size="md" variant="primary" leftIcon={<Icon name="Mail" size={13} color="#fff" />}
              onPress={() => onInvite({ email: email.trim(), role })} disabled={!email.trim()}>
              Send invite
            </PrimaryButton>
          </View>
        </View>
      </View>
    </View>
  );
}

// ─────────── NewTokenModal ───────────
export function NewTokenModal({ open, onCancel, onCreate }) {
  const [name, setName] = useState('');
  const [scope, setScope] = useState('read+write');
  const [env, setEnv] = useState('live');
  if (!open) return null;

  return (
    <View style={S.backdrop}>
      <Pressable style={StyleSheet.absoluteFill} onPress={onCancel} />
      <View style={[S.modalBox, Platform.OS === 'web' ? { width: 520 } as any : { width: '94%' }]}>
        <View style={S.modalHead}>
          <Text style={S.modalTitle}>Generate API token</Text>
          <TouchableOpacity onPress={onCancel} style={S.iconBtn}><Icon name="X" size={14} color={Colors.ink2} /></TouchableOpacity>
        </View>
        <View style={[S.modalBody, { gap: 16 }]}>
          <View style={S.field}>
            <View style={{ flexDirection: 'row' }}>
              <Text style={S.fieldLabel}>Token name</Text><RequiredStar />
            </View>
            <TextInput value={name} onChangeText={setName}
              placeholder="e.g. Production · backend service"
              placeholderTextColor={Colors.ink4} style={S.input} autoFocus />
            <Text style={S.fieldHint}>For your own bookkeeping. Shown in audit logs.</Text>
          </View>
          <View style={S.field}>
            <Text style={S.fieldLabel}>Scope</Text>
            <View style={{ flexDirection: 'row', gap: 8 }}>
              <PickerCard active={scope === 'read'}       title="Read-only"   sub="GET endpoints only" onPress={() => setScope('read')} />
              <PickerCard active={scope === 'read+write'} title="Read + write" sub="Full API access"    onPress={() => setScope('read+write')} />
            </View>
          </View>
          <View style={S.field}>
            <Text style={S.fieldLabel}>Environment</Text>
            <View style={{ flexDirection: 'row', gap: 8 }}>
              <PickerCard active={env === 'live'} title="Live" sub="sk_live_… · production data" onPress={() => setEnv('live')} />
              <PickerCard active={env === 'test'} title="Test" sub="sk_test_… · sandbox data"    onPress={() => setEnv('test')} />
            </View>
          </View>
          {/* Warning */}
          <View style={[S.infoBox, { backgroundColor: Colors.warnSoft, borderColor: Colors.warnLine }]}>
            <Icon name="Alert" size={13} color={Colors.warn} />
            <Text style={{ flex: 1, fontSize: 12, color: Colors.ink2, lineHeight: 17 }}>
              The token will be shown <Text style={{ fontWeight: FontWeight.semibold }}>once</Text> after creation. Copy it immediately — we don't store the plaintext.
            </Text>
          </View>
        </View>
        <View style={[S.modalFoot, { justifyContent: 'flex-end' }]}>
          <View style={{ flexDirection: 'row', gap: 8 }}>
            <PrimaryButton size="md" variant="default" onPress={onCancel}>Cancel</PrimaryButton>
            <PrimaryButton size="md" variant="primary" leftIcon={<Icon name="Check" size={13} color="#fff" />}
              onPress={() => onCreate({ name: name.trim(), scope, env })} disabled={!name.trim()}>
              Generate token
            </PrimaryButton>
          </View>
        </View>
      </View>
    </View>
  );
}

// ─────────── WebhookTestModal ───────────
const WEBHOOK_EVENTS_LIST = [
  'ticket.created','ticket.updated','ticket.resolved',
  'comment.created','comment.public','comment.internal',
  'delivery.sent','delivery.bounced','csat.received',
];

export function WebhookTestModal({ open, webhook, onClose }) {
  const [event, setEvent] = useState(webhook?.events?.[0] || 'ticket.created');
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);

  if (!open || !webhook) return null;

  const samplePayload = JSON.stringify({
    id: 'evt_abc123',
    event,
    created: new Date().toISOString(),
    data: {
      ticket: { id: 'TKT-1041', status: 'open', priority: 'high', subject: 'Billing discrepancy' },
      customer: { email: 'customer@example.com', name: 'Customer Name' },
    },
  }, null, 2);

  const run = () => {
    setRunning(true); setResult(null);
    setTimeout(() => {
      const ok = Math.random() > 0.2;
      const ms = 80 + Math.floor(Math.random() * 320);
      setResult({ ok, status: ok ? 200 : 500, ms, responseBody: ok ? '{"received":true}' : '{"error":"upstream timeout"}' });
      setRunning(false);
    }, 900);
  };

  return (
    <View style={S.backdrop}>
      <Pressable style={StyleSheet.absoluteFill} onPress={onClose} />
      <View style={[S.modalBox, Platform.OS === 'web' ? { width: 720, maxHeight: '90vh' } as any : { width: '96%' }]}>
        <View style={S.modalHead}>
          <View>
            <Text style={S.modalTitle}>Send test event</Text>
            <Text style={[S.inputMono, { fontSize: 11.5, color: Colors.ink3 }]}>POST {webhook.url}</Text>
          </View>
          <TouchableOpacity onPress={onClose} style={S.iconBtn}><Icon name="X" size={14} color={Colors.ink2} /></TouchableOpacity>
        </View>
        <ScrollView style={{ flex: 1 }} contentContainerStyle={[S.modalBody, { gap: 16 }]}>
          <View style={S.field}>
            <Text style={S.fieldLabel}>Event type</Text>
            <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 6 }}>
              {WEBHOOK_EVENTS_LIST.map(e => (
                <TouchableOpacity key={e} onPress={() => setEvent(e)}
                  style={[S.selectOption, event === e && S.selectOptionActive]}>
                  <Text style={[S.inputMono, { fontSize: 12, color: event === e ? Colors.ink : Colors.ink3 }]}>{e}</Text>
                </TouchableOpacity>
              ))}
            </View>
            <Text style={S.fieldHint}>We'll POST a sample payload of this event type to your endpoint.</Text>
          </View>
          <View>
            <Text style={[S.fieldLabel, { marginBottom: 6 }]}>Sample payload</Text>
            <View style={S.codeBlock}>
              <Text style={[S.inputMono, { fontSize: 11.5, color: Colors.ink, lineHeight: 18 }]}>{samplePayload}</Text>
            </View>
          </View>
          {result && (
            <View style={[S.alertBox, result.ok ? S.alertSuccess : S.alertDanger]}>
              <Icon name={result.ok ? 'CheckCircle' : 'XCircle'} size={16} color={result.ok ? Colors.success : Colors.danger} />
              <View>
                <Text style={{ fontSize: 13.5, fontWeight: FontWeight.semibold, color: result.ok ? Colors.success : Colors.danger }}>
                  {result.ok ? 'Delivered' : 'Failed'} · HTTP {result.status} · {result.ms}ms
                </Text>
                <Text style={[S.inputMono, { fontSize: 11.5, color: Colors.ink2, marginTop: 4 }]}>← {result.responseBody}</Text>
              </View>
            </View>
          )}
        </ScrollView>
        <View style={[S.modalFoot, { justifyContent: 'flex-end' }]}>
          <View style={{ flexDirection: 'row', gap: 8 }}>
            <PrimaryButton size="md" variant="default" onPress={onClose}>Close</PrimaryButton>
            <PrimaryButton size="md" variant="primary"
              leftIcon={<Icon name={running ? 'Refresh' : 'Send'} size={13} color="#fff" />}
              onPress={run} disabled={running}>
              {running ? 'Sending…' : 'Send test event'}
            </PrimaryButton>
          </View>
        </View>
      </View>
    </View>
  );
}

// ─────────── WebhookEditModal ───────────
export function WebhookEditModal({ open, webhook, onCancel, onSave }) {
  const [url, setUrl] = useState(webhook?.url || '');
  const [events, setEvents] = useState(webhook?.events || []);

  useEffect(() => {
    if (webhook) { setUrl(webhook.url || ''); setEvents(webhook.events || []); }
  }, [webhook]);

  if (!open) return null;

  const toggle = (e) => setEvents(es => es.includes(e) ? es.filter(x => x !== e) : [...es, e]);

  return (
    <View style={S.backdrop}>
      <Pressable style={StyleSheet.absoluteFill} onPress={onCancel} />
      <View style={[S.modalBox, Platform.OS === 'web' ? { width: 560 } as any : { width: '94%' }]}>
        <View style={S.modalHead}>
          <Text style={S.modalTitle}>Edit webhook</Text>
          <TouchableOpacity onPress={onCancel} style={S.iconBtn}><Icon name="X" size={14} color={Colors.ink2} /></TouchableOpacity>
        </View>
        <View style={[S.modalBody, { gap: 16 }]}>
          <View style={S.field}>
            <Text style={S.fieldLabel}>Endpoint URL</Text>
            <TextInput value={url} onChangeText={setUrl} placeholderTextColor={Colors.ink4}
              style={[S.input, S.inputMono]} />
          </View>
          <View>
            <Text style={[S.fieldLabel, { marginBottom: 8 }]}>Events</Text>
            <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 6 }}>
              {WEBHOOK_EVENTS_LIST.map(e => {
                const on = events.includes(e);
                return (
                  <TouchableOpacity key={e} onPress={() => toggle(e)}
                    style={[S.webhookEventChip, on && S.webhookEventChipOn]}>
                    <View style={[S.checkbox14, on && S.checkbox14On]}>
                      {on && <Icon name="Check" size={9} color="#fff" />}
                    </View>
                    <Text style={[S.inputMono, { fontSize: 12, color: on ? Colors.ink : Colors.ink3 }]}>{e}</Text>
                  </TouchableOpacity>
                );
              })}
            </View>
          </View>
        </View>
        <View style={[S.modalFoot, { justifyContent: 'flex-end' }]}>
          <View style={{ flexDirection: 'row', gap: 8 }}>
            <PrimaryButton size="md" variant="default" onPress={onCancel}>Cancel</PrimaryButton>
            <PrimaryButton size="md" variant="primary" leftIcon={<Icon name="Check" size={13} color="#fff" />}
              onPress={() => onSave({ url, events })} disabled={!url || events.length === 0}>
              Save webhook
            </PrimaryButton>
          </View>
        </View>
      </View>
    </View>
  );
}

// ─────────── RuleEditor ───────────
const TRIGGER_EVENTS = [
  { value: 'ticket.created',   label: 'Ticket created' },
  { value: 'ticket.updated',   label: 'Ticket updated' },
  { value: 'comment.created',  label: 'Comment created' },
  { value: 'priority.changed', label: 'Priority changed' },
  { value: 'status.changed',   label: 'Status changed' },
  { value: 'delivery.bounced', label: 'Email bounced' },
  { value: 'csat.received',    label: 'CSAT response received' },
  { value: 'sla.breach',       label: 'SLA breach warning' },
  { value: 'time.elapsed',     label: 'Time elapsed since last update' },
];

const CONDITION_FIELDS = [
  { value: 'category', label: 'Category' },
  { value: 'priority', label: 'Priority' },
  { value: 'channel',  label: 'Channel' },
  { value: 'status',   label: 'Status' },
  { value: 'tag',      label: 'Has tag' },
  { value: 'assignee', label: 'Assignee' },
  { value: 'subject',  label: 'Subject contains' },
];

const ACTION_TYPES = [
  { value: 'assign',     label: 'Assign to agent',       paramLabel: 'Agent',    inputType: 'select', options: ['priya','aditi','marcus','unassigned'] },
  { value: 'priority',   label: 'Set priority',          paramLabel: 'Level',    inputType: 'select', options: ['low','medium','high','urgent'] },
  { value: 'status',     label: 'Set status',            paramLabel: 'Status',   inputType: 'select', options: ['open','pending','resolved','closed'] },
  { value: 'tag',        label: 'Add tag',               paramLabel: 'Tag',      inputType: 'text' },
  { value: 'reply-to',   label: 'Set Reply-To address',  paramLabel: 'Email',    inputType: 'text' },
  { value: 'email',      label: 'Send email template',   paramLabel: 'Template', inputType: 'select', options: ['agent-reply','csat-request','ticket-resolved'] },
  { value: 'slack',      label: 'Post to Slack channel', paramLabel: 'Channel',  inputType: 'text' },
  { value: 'pause-sla',  label: 'Pause SLA timer',       paramLabel: '',         inputType: 'none' },
  { value: 'resume-sla', label: 'Resume SLA timer',      paramLabel: '',         inputType: 'none' },
  { value: 'close',      label: 'Close ticket',          paramLabel: '',         inputType: 'none' },
];

function triggerLabelFor(evt, conditions) {
  const ev = TRIGGER_EVENTS.find(e => e.value === evt)?.label || evt;
  if (!conditions || conditions.length === 0) return ev;
  const conds = conditions.map(c => {
    const field = CONDITION_FIELDS.find(f => f.value === c.field)?.label || c.field;
    return `${field} ${c.op || '='} ${c.value || '?'}`;
  });
  return `${ev} · ${conds.join(' · ')}`;
}

function actionLabelFor(action) {
  const meta = ACTION_TYPES.find(a => a.value === action.type);
  if (!meta) return JSON.stringify(action);
  if (meta.inputType === 'none') return meta.label;
  return `${meta.label}: ${action.value || '—'}`;
}

export function RuleEditor({ open, rule = {}, onCancel, onSave, onDelete }) {
  const [name, setName] = useState(rule.name || '');
  const [enabled, setEnabled] = useState(rule.enabled !== false);
  const [triggerEvent, setTriggerEvent] = useState(rule.triggerEvent || 'ticket.created');
  const [conditions, setConditions] = useState(rule._conditions || []);
  const [actions, setActions] = useState(rule._actionRows || [{ type: 'assign', value: 'priya' }]);

  useEffect(() => {
    if (rule) {
      setName(rule.name || ''); setEnabled(rule.enabled !== false);
      setTriggerEvent(rule.triggerEvent || 'ticket.created');
      setConditions(rule._conditions || []);
      setActions(rule._actionRows || [{ type: 'assign', value: 'priya' }]);
    }
  }, [rule?.id]);

  if (!open) return null;

  const setCond = (i, patch) => setConditions(cs => cs.map((c, j) => j === i ? { ...c, ...patch } : c));
  const setAction = (i, patch) => setActions(as => as.map((a, j) => j === i ? { ...a, ...patch } : a));

  const handleSave = () => {
    onSave({ ...rule, name: name.trim() || 'Untitled rule', enabled, triggerEvent, _conditions: conditions, _actionRows: actions });
  };

  const preview = `When ${triggerLabelFor(triggerEvent, conditions).toLowerCase()}, then ${actions.map(a => actionLabelFor(a).toLowerCase()).join(' and ')}.`;

  return (
    <View style={S.backdrop}>
      <Pressable style={StyleSheet.absoluteFill} onPress={onCancel} />
      <View style={[S.modalBox, Platform.OS === 'web' ? { width: 760, maxHeight: '92vh' } as any : { width: '98%' }]}>
        <View style={S.modalHead}>
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: 12 }}>
            <View style={S.iconPill}>
              <Icon name="Bolt" size={16} color={Colors.accentInk} />
            </View>
            <View>
              <Text style={S.modalTitle}>{rule._isNew ? 'New automation rule' : 'Edit rule'}</Text>
              <Text style={{ fontSize: 11.5, color: Colors.ink3, marginTop: 2 }}>When this happens, do these things.</Text>
            </View>
          </View>
          <TouchableOpacity onPress={onCancel} style={S.iconBtn}><Icon name="X" size={15} color={Colors.ink2} /></TouchableOpacity>
        </View>
        <ScrollView style={{ flex: 1 }} contentContainerStyle={[S.modalBody, { gap: 20 }]}>
          {/* Name + enabled */}
          <View style={{ flexDirection: 'row', gap: 16, alignItems: 'flex-end' }}>
            <View style={[S.field, { flex: 1 }]}>
              <Text style={S.fieldLabel}>Rule name</Text>
              <TextInput value={name} onChangeText={setName}
                placeholder="e.g. Auto-assign billing to Priya"
                placeholderTextColor={Colors.ink4} style={S.input} autoFocus />
            </View>
            <View style={{ alignItems: 'flex-end', paddingBottom: 4, gap: 4 }}>
              <Text style={{ fontSize: 11, fontWeight: FontWeight.bold, color: Colors.ink3, letterSpacing: 0.8 }}>STATUS</Text>
              <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
                <Toggle on={enabled} onPress={() => setEnabled(v => !v)} />
                <Text style={{ fontSize: 12.5, color: enabled ? Colors.success : Colors.ink3, fontWeight: FontWeight.medium }}>
                  {enabled ? 'Enabled' : 'Paused'}
                </Text>
              </View>
            </View>
          </View>

          {/* WHEN */}
          <SectionBlock label="When" colour={Colors.info}>
            <View style={{ gap: 10 }}>
              <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
                <Text style={{ fontSize: 12.5, color: Colors.ink3, width: 50 }}>Trigger</Text>
                <View style={{ flex: 1 }}>
                  <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 6 }}>
                    {TRIGGER_EVENTS.map(e => (
                      <TouchableOpacity key={e.value} onPress={() => setTriggerEvent(e.value)}
                        style={[S.selectOption, triggerEvent === e.value && S.selectOptionActive]}>
                        <Text style={[S.selectOptionText, triggerEvent === e.value && { color: Colors.ink, fontWeight: FontWeight.semibold }]}>{e.label}</Text>
                      </TouchableOpacity>
                    ))}
                  </View>
                </View>
              </View>
              {conditions.map((c, i) => (
                <View key={i} style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
                  <Text style={{ fontSize: 12.5, color: Colors.ink3, width: 50 }}>And</Text>
                  <View style={[S.selectBox, { flexDirection: 'row', flexWrap: 'nowrap', gap: 6, flex: 1, padding: 0, border: 'none' as any }]}>
                    <View style={{ flex: 1 }}>
                      <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 4 }}>
                        {CONDITION_FIELDS.slice(0,4).map(f => (
                          <TouchableOpacity key={f.value} onPress={() => setCond(i, { field: f.value })}
                            style={[S.selectOption, c.field === f.value && S.selectOptionActive, { paddingHorizontal: 8 }]}>
                            <Text style={[S.selectOptionText, c.field === f.value && { color: Colors.ink }]}>{f.label}</Text>
                          </TouchableOpacity>
                        ))}
                      </View>
                    </View>
                    <TextInput value={c.value || ''} onChangeText={(v) => setCond(i, { value: v })}
                      placeholder="value" placeholderTextColor={Colors.ink4}
                      style={[S.input, { height: 30, fontSize: 12, minWidth: 80 }]} />
                  </View>
                  <TouchableOpacity onPress={() => setConditions(cs => cs.filter((_,j) => j !== i))} style={S.iconBtn}>
                    <Icon name="X" size={13} color={Colors.ink3} />
                  </TouchableOpacity>
                </View>
              ))}
              <TouchableOpacity onPress={() => setConditions(cs => [...cs, { field: 'category', op: 'is', value: '' }])}>
                <Text style={{ color: Colors.info, fontSize: 12.5, fontWeight: FontWeight.medium }}>+ Add condition</Text>
              </TouchableOpacity>
            </View>
          </SectionBlock>

          {/* THEN */}
          <SectionBlock label="Then" colour={Colors.accent}>
            <View style={{ gap: 10 }}>
              {actions.map((a, i) => {
                const meta = ACTION_TYPES.find(t => t.value === a.type) || ACTION_TYPES[0];
                return (
                  <View key={i} style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
                    <Text style={{ fontSize: 12.5, color: Colors.ink3, width: 50 }}>{i === 0 ? 'Do' : 'And'}</Text>
                    <View style={{ flex: 1, flexDirection: 'row', gap: 6 }}>
                      <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 4, flex: 1 }}>
                        {ACTION_TYPES.slice(0,5).map(t => (
                          <TouchableOpacity key={t.value} onPress={() => setAction(i, { type: t.value, value: '' })}
                            style={[S.selectOption, a.type === t.value && S.selectOptionActive, { paddingHorizontal: 8 }]}>
                            <Text style={[S.selectOptionText, a.type === t.value && { color: Colors.ink }]}>{t.label}</Text>
                          </TouchableOpacity>
                        ))}
                      </View>
                      {meta.inputType === 'text' && (
                        <TextInput value={a.value || ''} onChangeText={(v) => setAction(i, { value: v })}
                          placeholder={meta.paramLabel} placeholderTextColor={Colors.ink4}
                          style={[S.input, { height: 30, fontSize: 12, minWidth: 100 }]} />
                      )}
                      {meta.inputType === 'none' && (
                        <Text style={{ fontSize: 12, color: Colors.ink4, fontStyle: 'italic', paddingHorizontal: 8, lineHeight: 30 }}>No parameter needed</Text>
                      )}
                    </View>
                    <TouchableOpacity onPress={() => setActions(as => as.filter((_,j) => j !== i))}
                      disabled={actions.length <= 1} style={S.iconBtn}>
                      <Icon name="X" size={13} color={Colors.ink3} />
                    </TouchableOpacity>
                  </View>
                );
              })}
              <TouchableOpacity onPress={() => setActions(as => [...as, { type: 'tag', value: '' }])}>
                <Text style={{ color: Colors.accent, fontSize: 12.5, fontWeight: FontWeight.medium }}>+ Add action</Text>
              </TouchableOpacity>
            </View>
          </SectionBlock>

          {/* Preview */}
          <View style={S.previewBox}>
            <Text style={S.previewLabel}>PREVIEW</Text>
            <Text style={{ fontSize: 13, color: Colors.ink, lineHeight: 20, marginTop: 6 }}>{preview}</Text>
          </View>
        </ScrollView>
        <View style={S.modalFoot}>
          {onDelete && (
            <PrimaryButton size="md" variant="ghost" leftIcon={<Icon name="X" size={13} color={Colors.danger} />}
              onPress={onDelete} textStyle={{ color: Colors.danger }}>Delete rule</PrimaryButton>
          )}
          <View style={{ flex: 1 }} />
          <Text style={[S.footMono, { marginRight: 8 }]}>
            {actions.length} action{actions.length === 1 ? '' : 's'} · {conditions.length} condition{conditions.length === 1 ? '' : 's'}
          </Text>
          <View style={{ flexDirection: 'row', gap: 8 }}>
            <PrimaryButton size="md" variant="default" onPress={onCancel}>Cancel</PrimaryButton>
            <PrimaryButton size="md" variant="primary" leftIcon={<Icon name="Check" size={13} color="#fff" />} onPress={handleSave}>
              {rule._isNew ? 'Create rule' : 'Save changes'}
            </PrimaryButton>
          </View>
        </View>
      </View>
    </View>
  );
}

// ─────────────────────────────────────────────────
//  STYLES
// ─────────────────────────────────────────────────
const S = StyleSheet.create({
  // Backdrop & modal box
  backdrop: {
    position: 'absolute' as any,
    top: 0, left: 0, right: 0, bottom: 0,
    backgroundColor: 'rgba(16,24,40,0.42)',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1000,
    padding: 24,
    ...(Platform.OS === 'web' ? { position: 'fixed' } as any : {}),
  },
  drawerBackdrop: {
    position: 'absolute' as any,
    top: 0, left: 0, right: 0, bottom: 0,
    backgroundColor: 'rgba(16,24,40,0.32)',
    flexDirection: 'row',
    justifyContent: 'flex-end',
    zIndex: 1000,
    ...(Platform.OS === 'web' ? { position: 'fixed' } as any : {}),
  },
  drawer: {
    width: 360,
    height: '100%' as any,
    backgroundColor: Colors.card,
    borderLeftWidth: 1,
    borderLeftColor: Colors.line,
    display: 'flex',
    flexDirection: 'column',
    ...Shadow.lg,
  },
  modalBox: {
    backgroundColor: Colors.card,
    borderRadius: Radii.r4,
    flexDirection: 'column',
    overflow: 'hidden',
    ...Shadow.lg,
    // Never wider than the viewport — caps every modal's fixed px width on
    // small screens so content is never clipped off the sides.
    ...(Platform.OS === 'web' ? { display: 'flex', maxWidth: '92vw' } as any : {}),
  },
  modalHead: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: 12,
    paddingHorizontal: 24,
    paddingTop: 20,
    paddingBottom: 16,
    borderBottomWidth: 1,
    borderBottomColor: Colors.line,
  },
  modalBody: {
    padding: 24,
    gap: 20,
  },
  modalFoot: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: 8,
    paddingHorizontal: 22,
    paddingVertical: 12,
    borderTopWidth: 1,
    borderTopColor: Colors.line,
    backgroundColor: Colors.paper2,
  },
  modalTitle: {
    fontSize: 21,
    fontWeight: FontWeight.bold,
    color: Colors.ink,
    letterSpacing: -0.3,
  },
  modalSubtitle: {
    fontSize: 12.5,
    color: Colors.ink3,
    marginTop: 3,
    lineHeight: 17,
  },
  backdropDim: {
    // Portrait-style focus: darker, softly blurred backdrop so the card pops.
    backgroundColor: 'rgba(10,14,24,0.66)',
    ...(Platform.OS === 'web'
      ? { backdropFilter: 'blur(7px) saturate(115%)', WebkitBackdropFilter: 'blur(7px) saturate(115%)' } as any
      : {}),
  },
  modalBoxPremium: {
    borderRadius: 18,
    ...(Platform.OS === 'web'
      ? { boxShadow: '0 36px 90px -20px rgba(10,14,24,0.55), 0 0 0 1px rgba(99,102,241,0.10)' } as any
      : {}),
  },
  modalHeadPremium: {
    backgroundColor: Colors.accentSoft,
    borderBottomColor: Colors.accentLine,
    ...(Platform.OS === 'web'
      ? { backgroundImage: 'linear-gradient(135deg, var(--accent-soft) 0%, var(--card) 100%)' } as any
      : {}),
  },
  modalHeadIcon: {
    width: 44, height: 44, borderRadius: 13,
    backgroundColor: Colors.card,
    borderWidth: 1, borderColor: Colors.accentLine,
    alignItems: 'center', justifyContent: 'center',
    flexShrink: 0,
    ...(Platform.OS === 'web'
      ? { boxShadow: '0 6px 16px rgba(79,70,229,0.22)' } as any
      : {}),
  },

  // Input
  input: {
    backgroundColor: Colors.card,
    borderWidth: 1,
    borderColor: Colors.lineStrong,
    borderRadius: Radii.r3,
    paddingHorizontal: 12,
    paddingVertical: 8,
    fontSize: FontSize.md,
    color: Colors.ink,
    height: 34,
    ...(Platform.OS === 'web' ? { outlineStyle: 'none', boxShadow: 'inset 0 1px 2px rgba(16,24,40,0.04)' } as any : {}),
  },
  inputMono: {
    fontFamily: Platform.OS === 'web' ? "'IBM Plex Mono', monospace" : undefined,
  },

  // Field
  field: { gap: 5 },
  fieldLabel: {
    fontSize: 13,
    fontWeight: FontWeight.semibold,
    color: Colors.ink,
  },
  fieldHint: {
    fontSize: 11.5,
    color: Colors.ink3,
  },
  fieldError: {
    fontSize: 11.5,
    color: Colors.danger,
  },

  // TagInput
  tagInputBox: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: 6,
    padding: 6,
    backgroundColor: Colors.card,
    borderWidth: 1,
    borderColor: Colors.line,
    borderRadius: Radii.r2,
    minHeight: 34,
  },
  tagChip: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: Colors.paper2,
    paddingHorizontal: 8,
    paddingVertical: 3,
    borderRadius: Radii.full,
    borderWidth: 1,
    borderColor: Colors.line,
    gap: 4,
  },
  tagChipText: { fontSize: 12, color: Colors.ink, fontWeight: FontWeight.medium },
  tagDraftInput: {
    flex: 1,
    minWidth: 80,
    fontSize: 13,
    color: Colors.ink,
    borderWidth: 0,
    padding: 0,
    height: 22,
    ...(Platform.OS === 'web' ? { outlineStyle: 'none' } as any : {}),
  },
  tagHint: { fontSize: 11.5, color: Colors.ink3, marginTop: 6 },

  // MultiInput
  multiRow: { flexDirection: 'row', alignItems: 'center', gap: 6 },

  // Segmented picker
  segmented: {
    flexDirection: 'row',
    backgroundColor: Colors.paper2,
    borderWidth: 1,
    borderColor: Colors.line,
    borderRadius: Radii.r2,
    padding: 2,
  },
  segBtn: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 5,
    paddingHorizontal: 8,
    borderRadius: Radii.r1,
    gap: 4,
  },
  segBtnActive: {
    backgroundColor: Colors.card,
    ...Shadow.sm,
  },
  segBtnText: { fontSize: 12, fontWeight: FontWeight.medium, color: Colors.ink3 },
  segBtnTextActive: { fontWeight: FontWeight.semibold, color: Colors.ink },

  // PickerCard
  pickerCard: {
    flex: 1,
    padding: 14,
    backgroundColor: Colors.card,
    borderWidth: 1.5,
    borderColor: Colors.lineStrong,
    borderRadius: Radii.r3,
    position: 'relative' as any,
  },
  pickerCardActive: {
    backgroundColor: Colors.infoSoft,
    borderColor: Colors.info,
  },
  pickerCardTitle: { fontSize: 14, fontWeight: FontWeight.semibold, color: Colors.ink, marginBottom: 4 },
  pickerCardSub: { fontSize: 12, color: Colors.ink3 },
  pickerBadge: {
    position: 'absolute' as any, top: 8, right: 10,
    backgroundColor: Colors.paper3,
    paddingHorizontal: 6, paddingVertical: 1,
    borderRadius: 99,
  },
  pickerBadgeText: { fontSize: 10, fontWeight: FontWeight.semibold, color: Colors.ink3 },

  // Toggle
  toggle: {
    width: 32, height: 18, borderRadius: 999,
    backgroundColor: Colors.paper3,
    borderWidth: 1, borderColor: Colors.lineStrong,
    justifyContent: 'center', paddingHorizontal: 1,
  },
  toggleOn: { backgroundColor: Colors.accent, borderColor: Colors.accent },
  toggleThumb: {
    width: 14, height: 14, borderRadius: 99,
    backgroundColor: Colors.white,
    ...Shadow.sm,
    alignSelf: 'flex-start',
  },
  toggleThumbOn: { alignSelf: 'flex-end' },

  // Checkbox
  checkbox: {
    width: 16, height: 16, borderRadius: 3,
    borderWidth: 1.5, borderColor: Colors.lineStrong,
    backgroundColor: Colors.card,
    justifyContent: 'center', alignItems: 'center',
    flexShrink: 0,
  },
  checkboxOn: { backgroundColor: Colors.accent, borderColor: Colors.accent },
  checkboxInfo: { backgroundColor: Colors.info, borderColor: Colors.info },
  checkbox14: {
    width: 14, height: 14, borderRadius: 3,
    borderWidth: 1.5, borderColor: Colors.lineStrong,
    backgroundColor: Colors.card,
    justifyContent: 'center', alignItems: 'center',
    flexShrink: 0,
  },
  checkbox14On: { backgroundColor: Colors.accent, borderColor: Colors.accent },
  checkbox18: {
    width: 18, height: 18, borderRadius: 3, marginTop: 1,
    borderWidth: 1.5, borderColor: Colors.lineStrong,
    backgroundColor: Colors.card,
    justifyContent: 'center', alignItems: 'center',
    flexShrink: 0,
  },
  checkbox18On: { backgroundColor: Colors.ink, borderColor: Colors.ink },

  // Check row (FilterDrawer)
  checkRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    paddingVertical: 6,
    paddingHorizontal: 8,
    borderRadius: Radii.r2,
  },
  checkRowText: { fontSize: 13, color: Colors.ink2 },

  // Filter group label
  filterGroupLabel: {
    fontSize: 11, fontWeight: FontWeight.bold, color: Colors.ink3,
    letterSpacing: 0.8, marginBottom: 8,
  },

  // Icon button
  iconBtn: {
    width: 28, height: 28,
    justifyContent: 'center', alignItems: 'center',
    borderRadius: Radii.r2,
  },

  // SectionLabel (export)
  sectionLabel: {
    fontSize: 13, fontWeight: FontWeight.semibold,
    color: Colors.ink, marginBottom: 10,
  },

  // ColumnPicker
  columnPicker: {
    borderWidth: 1, borderColor: Colors.line,
    borderRadius: Radii.r3, padding: 14,
  },
  columnPickerHead: {
    flexDirection: 'row', alignItems: 'center', marginBottom: 10,
  },
  columnPickerHeadText: { fontSize: 12, fontWeight: FontWeight.semibold, color: Colors.ink2 },
  columnGroupLabel: {
    fontSize: 10.5, fontWeight: FontWeight.bold, color: Colors.ink4,
    letterSpacing: 0.8, marginBottom: 8,
  },
  columnGrid: {
    flexDirection: 'row', flexWrap: 'wrap', rowGap: 6, columnGap: 16,
  },
  columnItem: {
    flexDirection: 'row', alignItems: 'center', gap: 10,
    width: '48%',
  },

  // Info box / alert
  infoBox: {
    flexDirection: 'row', gap: 12, alignItems: 'flex-start',
    padding: 14, backgroundColor: Colors.paper2,
    borderWidth: 1, borderColor: Colors.line,
    borderRadius: Radii.r3,
  },
  alertBox: {
    flexDirection: 'row', gap: 12, alignItems: 'flex-start',
    padding: 12, borderRadius: Radii.r3, borderWidth: 1,
  },
  alertSuccess: { backgroundColor: Colors.successSoft, borderColor: Colors.successLine },
  alertDanger:  { backgroundColor: Colors.dangerSoft,  borderColor: Colors.dangerLine },

  // Icon pill (modal headers)
  iconPill: {
    width: 32, height: 32, borderRadius: Radii.r2,
    backgroundColor: Colors.accentSoft,
    justifyContent: 'center', alignItems: 'center',
  },

  // Lead opt-in
  leadOptIn: {
    flexDirection: 'row', alignItems: 'flex-start', gap: 12,
    padding: 14, backgroundColor: Colors.paper2,
    borderWidth: 1, borderColor: Colors.line,
    borderRadius: Radii.r3,
  },

  // Select box (chip-style select)
  selectBox: {
    flexDirection: 'row', flexWrap: 'wrap', gap: 6,
  },
  selectOption: {
    paddingHorizontal: 10, paddingVertical: 5,
    borderRadius: Radii.r2,
    borderWidth: 1, borderColor: Colors.line,
    backgroundColor: Colors.paper2,
  },
  selectOptionActive: {
    backgroundColor: Colors.infoSoft,
    borderColor: Colors.info,
  },
  selectOptionText: {
    fontSize: 13, color: Colors.ink3,
  },

  // Toggle row
  toggleRow: {
    flexDirection: 'row', alignItems: 'center', gap: 10,
    paddingHorizontal: 12, paddingVertical: 8,
    backgroundColor: Colors.paper2,
    borderWidth: 1, borderColor: Colors.line,
    borderRadius: Radii.r2, height: 38,
  },

  // Options box (FieldEditor)
  optionsBox: {
    borderWidth: 1, borderColor: Colors.line,
    borderRadius: Radii.r2, padding: 10,
    backgroundColor: Colors.paper2,
  },
  optionRow: {
    flexDirection: 'row', alignItems: 'center', gap: 8,
    paddingVertical: 4, paddingHorizontal: 8,
    backgroundColor: Colors.card,
    borderWidth: 1, borderColor: Colors.line,
    borderRadius: Radii.r2, marginBottom: 4,
  },
  optionIdx: {
    fontFamily: Platform.OS === 'web' ? 'monospace' : undefined,
    fontSize: 11, color: Colors.ink4,
  },

  // Preview box
  previewBox: {
    padding: 12, backgroundColor: Colors.paper2,
    borderWidth: 1, borderColor: Colors.line,
    borderRadius: Radii.r3,
  },
  previewLabel: {
    fontSize: 10.5, fontWeight: FontWeight.bold, color: Colors.ink4,
    letterSpacing: 0.8,
  },

  // Footer mono text
  footMono: {
    fontFamily: Platform.OS === 'web' ? "'IBM Plex Mono', monospace" : undefined,
    fontSize: 11.5, color: Colors.ink3, flex: 1,
  },

  // SectionBlock
  sectionBlock: {
    borderWidth: 1, borderRadius: Radii.r3, overflow: 'hidden',
  },
  sectionBlockHead: {
    flexDirection: 'row', alignItems: 'center', gap: 8,
    paddingHorizontal: 14, paddingVertical: 8,
    borderBottomWidth: 1, borderBottomColor: Colors.line,
  },
  sectionBlockDot: { width: 6, height: 6, borderRadius: 99 },
  sectionBlockLabel: {
    fontSize: 11.5, fontWeight: FontWeight.bold, color: Colors.ink2,
    letterSpacing: 0.8,
  },

  // Code block (webhook payload)
  codeBlock: {
    padding: 12, backgroundColor: Colors.paper2,
    borderWidth: 1, borderColor: Colors.line,
    borderRadius: Radii.r2, maxHeight: 200, overflow: 'hidden',
  },

  // Webhook event chip
  webhookEventChip: {
    flexDirection: 'row', alignItems: 'center', gap: 6,
    paddingHorizontal: 8, paddingVertical: 6,
    borderRadius: Radii.r2, backgroundColor: Colors.paper2,
  },
  webhookEventChipOn: { backgroundColor: Colors.accentSoft },

  // Dropdown (contact picker)
  dropdown: {
    position: 'absolute' as any,
    top: '100%', left: 0, right: 0,
    zIndex: 40,
    elevation: 12,
    backgroundColor: Colors.card,
    borderWidth: 1, borderColor: Colors.lineStrong,
    borderRadius: Radii.r4,
    ...Shadow.lg,
    overflow: 'hidden',
    marginTop: 6,
  },
  dropdownRow: {
    flexDirection: 'row', alignItems: 'center', gap: 11,
    paddingVertical: 11, paddingHorizontal: 13,
    borderBottomWidth: 1, borderBottomColor: Colors.line,
    ...(Platform.OS === 'web' ? { cursor: 'pointer' } as any : {}),
  },
  dropdownFoot: {
    flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between',
    paddingVertical: 7, paddingHorizontal: 13,
    borderTopWidth: 1, borderTopColor: Colors.line,
    backgroundColor: Colors.paper2,
  },

  // ─── Add-customer card (New-ticket → Customer) ───
  noMatchRow: {
    flexDirection: 'row', alignItems: 'center', gap: 12,
    marginTop: 10, padding: 12,
    backgroundColor: Colors.paper2,
    borderWidth: 1, borderColor: Colors.line, borderStyle: 'dashed' as any,
    borderRadius: Radii.r3,
  },
  noMatchTitle: { fontSize: 13, fontWeight: FontWeight.semibold, color: Colors.ink },
  noMatchSub: { fontSize: 11.5, color: Colors.ink3, marginTop: 1 },
  selectedCustomerRow: {
    flexDirection: 'row', alignItems: 'center', gap: 9,
    marginTop: 10, paddingVertical: 9, paddingHorizontal: 12,
    backgroundColor: Colors.successSoft || Colors.accentSoft,
    borderWidth: 1, borderColor: Colors.success, borderRadius: Radii.r3,
  },
  selectedCustomerTick: {
    width: 18, height: 18, borderRadius: 9,
    backgroundColor: Colors.success,
    alignItems: 'center', justifyContent: 'center',
  },
  selectedCustomerText: { flex: 1, fontSize: 12.5, color: Colors.ink2 },
  addCard: {
    marginTop: 12,
    backgroundColor: Colors.card,
    borderWidth: 1, borderColor: Colors.accentLine,
    borderRadius: 16,
    overflow: 'hidden',
    ...Shadow.lg,
    ...(Platform.OS === 'web'
      ? { boxShadow: '0 18px 48px -12px rgba(79,70,229,0.28), 0 4px 14px rgba(16,24,40,0.08)' } as any
      : {}),
  },
  addCardAccent: {
    height: 3,
    backgroundColor: Colors.accent,
    ...(Platform.OS === 'web'
      ? { backgroundImage: 'linear-gradient(90deg, #6366F1 0%, #8B5CF6 45%, #6366F1 100%)' } as any
      : {}),
  },
  addCardHead: {
    flexDirection: 'row', alignItems: 'center', gap: 12,
    paddingHorizontal: 18, paddingTop: 16, paddingBottom: 14,
    borderBottomWidth: 1, borderBottomColor: Colors.accentLine,
    backgroundColor: Colors.accentSoft,
    ...(Platform.OS === 'web'
      ? { backgroundImage: 'linear-gradient(135deg, var(--accent-soft) 0%, var(--card) 100%)' } as any
      : {}),
  },
  addCardIcon: {
    width: 38, height: 38, borderRadius: 11,
    backgroundColor: Colors.card,
    borderWidth: 1, borderColor: Colors.accentLine,
    alignItems: 'center', justifyContent: 'center', flexShrink: 0,
    ...(Platform.OS === 'web'
      ? { boxShadow: '0 4px 10px rgba(79,70,229,0.18)' } as any
      : {}),
  },
  addCardTitle: { fontSize: 15.5, fontWeight: FontWeight.bold, color: Colors.ink, letterSpacing: -0.3 },
  addCardSub: { fontSize: 11.5, color: Colors.ink3, marginTop: 2, lineHeight: 16 },
  addCardBody: { paddingHorizontal: 18, paddingTop: 14, paddingBottom: 8, gap: 11 },
  addCardErr: {
    fontSize: 12, color: Colors.danger, marginTop: 2,
    backgroundColor: Colors.dangerSoft, borderRadius: Radii.r2,
    borderWidth: 1, borderColor: Colors.dangerLine || Colors.danger,
    paddingVertical: 8, paddingHorizontal: 11,
  },
  addCardFoot: {
    flexDirection: 'row', alignItems: 'center', justifyContent: 'flex-end', gap: 8,
    paddingHorizontal: 18, paddingVertical: 13,
    borderTopWidth: 1, borderTopColor: Colors.line,
    backgroundColor: Colors.paper2,
  },
});
